<template>
  <v-app>
    <!-- ヘッダー -->
    <v-app-bar color="primary" app fixed class="header-container" elevation="1">
      <v-container class="pa-0 header-content-container">
        <v-row align="center" class="ma-0" style="height: 64px;">
          <v-col cols="12" class="pa-0">
            <div class="header-inner-flex">
              <!-- ロゴを左側に固定（クリックでサービストップへ） -->
              <a href="/" class="logo-link" title="ブクマすトップへ">
                <img src="./assets/logo.svg" alt="BKMS" height="22" style="filter: brightness(0) invert(1); margin-left: 16px;">
              </a>
              
              <!-- バッジを画面中央に絶対配置 -->
              <div class="mode-badge-absolute">
                <v-chip
                  v-if="currentMode !== 'normal'"
                  :color="currentMode === 'sort' ? 'orange' : 'green'"
                  variant="elevated"
                  size="small"
                  class="mode-badge"
                >
                  {{ modeLabels[currentMode] }}
                </v-chip>
              </div>
              
              <!-- 中央のスペーサー -->
              <v-spacer></v-spacer>
              
              <!-- 公開閲覧モード：バッジ表示（2行：タイトル＋製作者情報） -->
              <div v-if="isPublicViewMode" class="public-badge-container" style="margin-right: 16px;">
                <v-chip
                  color="blue"
                  variant="elevated"
                  size="small"
                  class="public-badge-chip"
                >
                  <div class="public-badge-content">
                    <span class="public-badge-title">公開ブクマす</span>
                    <span class="public-badge-creator">
                      <v-icon size="12" class="public-badge-sns-icon">{{ publicViewSnsIcon }}</v-icon>
                      <a v-if="publishSnsUrl && publishSnsUsername" :href="publishSnsUrl" target="_blank" rel="noopener noreferrer" class="public-badge-creator-link" @click.stop>@{{ publishSnsUsername }}</a>
                      <span v-else class="public-badge-creator-unknown">製作者：不明</span>
                    </span>
                  </div>
                </v-chip>
              </div>

              <!-- バーガーメニューを右側に固定（ロゴと対称になるよう余白調整） -->
              <v-menu v-if="!isPublicViewMode">
                <template v-slot:activator="{ props }">
                  <v-btn icon v-bind="props" color="#FFF" style="margin-right: 16px;">
                    <v-icon>mdi-menu</v-icon>
                  </v-btn>
                </template>
        <v-list bg-color="white" class="custom-menu-list">
          <!-- スマートフォン操作性改善：モード切替メニュー -->
          <template v-if="isMobile">
            <v-list-item @click="setMode('sort')" :disabled="currentMode === 'edit'">
              <template v-slot:prepend><IconArrowsSort :size="20" class="menu-icon" /></template>
              <v-list-item-title>並び替え</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setMode('edit')" :disabled="currentMode === 'sort'">
              <template v-slot:prepend><IconPencil :size="20" class="menu-icon" /></template>
              <v-list-item-title>リンク編集</v-list-item-title>
            </v-list-item>
            <v-divider v-if="currentMode === 'normal'"></v-divider>
          </template>
          
          <v-list-item @click="exportData" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconFileExport :size="20" class="menu-icon" /></template>
            <v-list-item-title>データエクスポート</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showImportDialog" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconFileImport :size="20" class="menu-icon" /></template>
            <v-list-item-title>データインポート</v-list-item-title>
          </v-list-item>
          <!-- クラウド関連メニュー追加 -->
          <v-list-item :disabled="isCloudSyncActive || currentMode !== 'normal'" @click="!(isCloudSyncActive || currentMode !== 'normal') && showCloudSaveDialog()">
            <template v-slot:prepend><component :is="isCloudSyncActive ? 'IconCloudCheck' : 'IconCloudUpload'" :size="20" class="menu-icon" /></template>
            <v-list-item-title>{{ isCloudSyncActive ? 'クラウド同期中' : 'クラウドに保存' }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showCloudManageDialog" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconCloudCog :size="20" class="menu-icon" /></template>
            <v-list-item-title>クラウドデータ管理</v-list-item-title>
          </v-list-item>
          <v-list-item @click="resetAllTiles" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconTrash :size="20" class="menu-icon" /></template>
            <v-list-item-title>データ全削除</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <!-- ブックマーク公開機能 (F-021) -->
          <v-list-subheader>公開設定</v-list-subheader>
          <v-list-item
            v-if="!isPublished"
            @click="showPublishSettings"
            :disabled="!isCloudSyncActive || currentMode !== 'normal'"
          >
            <template v-slot:prepend><IconWorld :size="20" class="menu-icon" /></template>
            <v-list-item-title>このブクマすを公開する</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="isPublished"
            @click="unpublishBookmarksAction"
            :disabled="currentMode !== 'normal'"
          >
            <template v-slot:prepend><IconWorldOff :size="20" class="menu-icon" /></template>
            <v-list-item-title>公開中のブクマすを非公開にする</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="isPublished"
            @click="showPublishUrlModal"
            :disabled="currentMode !== 'normal'"
          >
            <template v-slot:prepend><IconLink :size="20" class="menu-icon" /></template>
            <v-list-item-title>公開中のブクマすURLを表示</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="hasAnyPublishedPages"
            @click="showPublishedPagesList"
            :disabled="currentMode !== 'normal'"
          >
            <template v-slot:prepend><IconList :size="20" class="menu-icon" /></template>
            <v-list-item-title>公開中のブクマす一覧</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-subheader>ブックマークページ</v-list-subheader>
          <v-list-item @click="createNewBookmarkPage" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconFilePlus :size="20" class="menu-icon" /></template>
            <v-list-item-title>新しいページを作成</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showBookmarkPagesModal" :disabled="currentMode !== 'normal' || bookmarkPages.length === 0">
            <template v-slot:prepend><IconFiles :size="20" class="menu-icon" /></template>
            <v-list-item-title>ページを切り替え</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showAboutPage" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconInfoCircle :size="20" class="menu-icon" /></template>
            <v-list-item-title>ブクマすについて</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showPrivacyPolicy" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><IconShieldLock :size="20" class="menu-icon" /></template>
            <v-list-item-title>プライバシーポリシー</v-list-item-title>
          </v-list-item>
          <v-list-item @click="openContactPage" :disabled="currentMode !== 'normal'">
            <template v-slot:prepend><MailIcon :size="20" class="menu-icon" /></template>
            <v-list-item-title>お問い合わせ</v-list-item-title>
          </v-list-item>
          
          <!-- ログレベル設定メニュー (開発環境のみ表示) -->
          <template v-if="isDevelopment">
            <v-divider></v-divider>
            <v-list-subheader>ログ設定</v-list-subheader>
            <v-list-item @click="setLogLevel('DEBUG')">
              <template v-slot:prepend><IconBug :size="20" class="menu-icon" /></template>
              <v-list-item-title :class="{'font-weight-bold': getLogLevel() === 'DEBUG'}">すべて表示</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setLogLevel('INFO')">
              <template v-slot:prepend><IconInfoSmall :size="20" class="menu-icon" /></template>
              <v-list-item-title :class="{'font-weight-bold': getLogLevel() === 'INFO'}">情報以上</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setLogLevel('WARN')">
              <template v-slot:prepend><IconAlertTriangle :size="20" class="menu-icon" /></template>
              <v-list-item-title :class="{'font-weight-bold': getLogLevel() === 'WARN'}">警告以上</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setLogLevel('ERROR')">
              <template v-slot:prepend><IconAlertCircle :size="20" class="menu-icon" /></template>
              <v-list-item-title :class="{'font-weight-bold': getLogLevel() === 'ERROR'}">エラーのみ</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setLogLevel('NONE')">
              <template v-slot:prepend><IconEyeOff :size="20" class="menu-icon" /></template>
              <v-list-item-title :class="{'font-weight-bold': getLogLevel() === 'NONE'}">ログなし</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <!-- メインコンテンツ -->
    <v-main>
      <v-container class="pa-0">
        <!-- 検索コンテナ（公開閲覧モードでは非表示） -->
        <v-row v-if="!isPublicViewMode" justify="center" class="ma-0">
          <v-col cols="12" sm="10" md="8" lg="6" xl="5" class="pa-0 search-col">
            <div class="custom-search-container">
              <div class="engine-select">
                <span>{{ searchEngine === 'google' ? 'Google' : searchEngine === 'bing' ? 'Bing' : 'ChatGPT' }}</span>
                <span class="dropdown-arrow">▼</span>
                <select v-model="searchEngine" class="hidden-select">
                  <option value="google">Google</option>
                  <option value="bing">Bing</option>
                  <option value="chatgpt">ChatGPT</option>
                </select>
              </div>
              <div class="divider"></div>
              <div class="search-input-area">
                <input 
                  type="text" 
                  v-model="searchQuery" 
                  placeholder="検索ワードを入力（Enterで検索）" 
                  class="custom-input"
                  @keyup.enter="executeSearch"
                />
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- オフラインモードバナー -->
        <v-row v-if="isCloudOfflineMode" class="ma-0 mb-1">
          <v-col cols="12" class="pa-0">
            <v-alert type="warning" variant="tonal" density="compact" closable class="mx-2">
              <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
                <span>ネットワークエラーのため、ローカルデータを表示中です。クラウド同期は一時停止しています。</span>
                <v-btn size="small" variant="outlined" color="warning" @click="retryCloudFetch">
                  <v-icon start size="small">mdi-refresh</v-icon>
                  再試行
                </v-btn>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- クラウド取得失敗エラー画面（ローカルデータなし時） -->
        <v-row v-if="cloudFetchFailed" class="ma-0">
          <v-col cols="12" class="pa-0 d-flex justify-center">
            <v-card class="mx-4 my-8 pa-6" max-width="480" variant="outlined" style="border-color: #e9d0ca;">
              <div class="text-center">
                <v-icon size="64" color="warning" class="mb-4">mdi-cloud-off-outline</v-icon>
                <h3 class="text-h6 mb-2">クラウドデータを取得できません</h3>
                <p class="text-body-2 mb-4" style="color: #666;">
                  ネットワーク接続を確認してください。<br>
                  クラウドにはデータが保存されています。<br>
                  接続が回復したら再試行してください。
                </p>
                <div v-if="cloudFetchRetrying" class="mb-4">
                  <v-progress-circular indeterminate color="primary" size="24" class="mr-2"></v-progress-circular>
                  <span class="text-body-2">{{ cloudFetchRetryInfo }}</span>
                </div>
                <div class="d-flex justify-center" style="gap: 12px;">
                  <v-btn color="primary" variant="elevated" @click="retryCloudFetch" :loading="cloudFetchRetrying">
                    <v-icon start>mdi-refresh</v-icon>
                    再試行する
                  </v-btn>
                  <v-btn variant="outlined" @click="goToHome">
                    <v-icon start>mdi-home</v-icon>
                    トップに戻る
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- タイル一覧 -->
        <v-row v-if="!cloudFetchFailed" class="ma-0">
          <v-col cols="12" class="pa-0">
            <div class="tiles-container-wrapper"
              @mousemove="handleDockMouseMove"
              @mouseleave="handleDockMouseLeave"
            >
              <!-- モバイルの場合：モードに応じた表示 -->
              <template v-if="isMobile">
                <!-- 並び替えモード：draggableを使用 -->
                <draggable 
                  v-if="currentMode === 'sort'"
                  v-model="bookmarks" 
                  class="tiles-container mobile-tiles"
                  item-key="id"
                  :animation="200"
                  ghost-class="ghost-tile"
                  @start="onDragStart"
                  @end="onDragEnd"
                  :delay="0"
                  :delayOnTouchOnly="true"
                  :disabled="false"
                >
                  <template #item="{element}">
                    <BookmarkTile 
                      :id="String(element.id || '')"
                      :name="element.name"
                      :icon-url="element.iconUrl || '/bookmark.svg'"
                      :icon-color="'#AAA'"
                      :backgroundColor="element.color || '#f0f0f0'"
                      :isEmpty="element.isEmpty"
                      :isMobile="isMobile"
                      :url="element.url"
                      :currentMode="currentMode"
                      :customIcon="element.customIcon || ''"
                      :readOnly="isPublicViewMode"
                      @click="handleTileClick(element)"
                      @edit="editTileContent(element)"
                      @delete="clearTileContent(element)"
                    />
                  </template>
                </draggable>
                
                <!-- 通常モード・リンク編集モード：通常表示 -->
                <div v-else class="tiles-container mobile-tiles">
                  <div v-for="element in bookmarks" :key="element.id">
                    <BookmarkTile 
                      :id="String(element.id || '')"
                      :name="element.name"
                      :icon-url="element.iconUrl || '/bookmark.svg'"
                      :icon-color="'#AAA'"
                      :backgroundColor="element.color || '#f0f0f0'"
                      :isEmpty="element.isEmpty"
                      :isMobile="isMobile"
                      :url="element.url"
                      :currentMode="currentMode"
                      :customIcon="element.customIcon || ''"
                      :readOnly="isPublicViewMode"
                      @click="handleTileClick(element)"
                      @edit="editTileContent(element)"
                      @delete="clearTileContent(element)"
                    />
                  </div>
                </div>
              </template>
              <!-- PC環境ではdraggableを使用 -->
              <draggable 
                v-else
                v-model="bookmarks" 
                class="tiles-container"
                item-key="id"
                :animation="200"
                ghost-class="ghost-tile"
                @start="onDragStart"
                @end="onDragEnd"
                :delay="0"
                :delayOnTouchOnly="true"
                :disabled="false"
              >
                <template #item="{element}">
                  <BookmarkTile 
                    :id="String(element.id || '')"
                    :name="element.name"
                    :icon-url="element.iconUrl || '/bookmark.svg'"
                    :icon-color="'#AAA'"
                    :backgroundColor="element.color || '#f0f0f0'"
                    :isEmpty="element.isEmpty"
                    :isMobile="isMobile"
                    :url="element.url"
                    :currentMode="'normal'"
                    :customIcon="element.customIcon || ''"
                    :readOnly="isPublicViewMode"
                    @click="handleTileClick(element)"
                    @edit="editTileContent(element)"
                    @delete="clearTileContent(element)"
                  />
                </template>
              </draggable>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- ブックマーク編集モーダル -->
    <BookmarkModal 
      :isOpen="isModalOpen" 
      :editTile="currentEditTile"
      :customColors="customColors"
      @close="closeModal"
      @save="saveBookmark"
      @delete="deleteBookmarkFromModal"
      @update-custom-colors="updateCustomColors"
    />

    <!-- インポート用の非表示ファイル入力 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileImport" 
      accept=".json" 
      style="display: none"
    />

    <!-- クラウド関連のモーダル -->
    <!-- PINコードモーダル -->
    <PinCodeModal
      :isOpen="isPinCodeModalOpen"
      :isAuthMode="isAuthMode"
      @save="handlePinCodeSave"
      @close="closePinCodeModal"
    />

    <!-- ユニークURLモーダル -->
    <UniqueUrlModal
      :isOpen="isUniqueUrlModalOpen"
      :uniqueUrl="uniqueUrl"
      @close="closeUniqueUrlModal"
    />

    <!-- クラウドデータ管理モーダル -->
    <CloudDataModal
      :isOpen="isCloudDataModalOpen"
      @close="closeCloudDataModal"
      @delete="deleteCloudData"
    />

    <!-- クラウド vs ローカル 競合解決ダイアログ -->
    <v-dialog v-model="isConflictDialogOpen" max-width="480" persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap: 8px;">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          データの競合が検出されました
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            オフライン中にローカルデータが変更されましたが、クラウドには異なるデータが保存されています。
            どちらのデータを使用しますか？
          </p>
          <v-alert type="info" variant="tonal" density="compact" class="mb-2">
            <strong>クラウドデータ</strong>：他のデバイスで更新された最新のデータです。
          </v-alert>
          <v-alert type="info" variant="tonal" density="compact">
            <strong>ローカルデータ</strong>：このデバイスでオフライン中に編集したデータです。選択するとクラウドに上書き同期されます。
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" color="primary" @click="resolveConflictWithLocal">
            <v-icon start>mdi-cellphone</v-icon>
            ローカルデータを使う
          </v-btn>
          <v-btn variant="elevated" color="primary" @click="resolveConflictWithCloud">
            <v-icon start>mdi-cloud-download</v-icon>
            クラウドデータを使う
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- クラウドデータ読み込み中のローディングオーバーレイ -->
    <div v-if="cloudSaveLoading" class="loading-overlay">
      <div class="loading-spinner-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">クラウドデータを読み込んでいます...</div>
      </div>
    </div>
    
    <!-- スマートフォン操作性改善：完了ボタン -->
    <v-btn
      v-if="isMobile && currentMode !== 'normal'"
      :color="currentMode === 'sort' ? 'orange' : 'green'"
      variant="elevated"
      size="large"
      @click="setMode('normal')"
      class="complete-button"
      :class="`complete-button--${currentMode}`"
      prepend-icon="mdi-check"
      rounded="pill"
    >
      <span class="complete-button__text">完了</span>
    </v-btn>
    
    <!-- プライバシーポリシーモーダル -->
    <PrivacyPolicyModal
      v-if="isPrivacyPolicyOpen"
      @close="closePrivacyPolicy"
    />

    <!-- BKMSについてモーダル -->
    <AboutPage 
      v-if="isAboutPageOpen"
      @close="closeAboutPage"
    />

    <!-- ブックマークページ管理モーダル -->
    <BookmarkPagesModal
      :isOpen="isBookmarkPagesModalOpen"
      :pages="bookmarkPages"
      :currentPageId="currentPageId"
      @close="isBookmarkPagesModalOpen = false"
      @switch-page="switchBookmarkPage"
      @rename-page="renameBookmarkPage"
      @create-new-page="createNewBookmarkPage"
    />

    <!-- 利用規約同意モーダル -->
    <TermsModal
      :isOpen="isTermsModalOpen"
      @accept="acceptTerms"
    />

    <!-- ブックマーク公開設定モーダル (F-021) -->
    <PublishSettingsModal
      :isOpen="isPublishSettingsOpen"
      @close="isPublishSettingsOpen = false"
      @publish="handlePublishBookmarks"
    />

    <!-- 公開URL通知モーダル (F-021) -->
    <PublishUrlModal
      :isOpen="isPublishUrlModalOpen"
      :publishUrl="publishUrl"
      :publishedPagesList="[]"
      @close="isPublishUrlModalOpen = false"
    />

    <!-- 公開中ブクマす一覧モーダル (F-021) -->
    <PublishedPagesListModal
      :isOpen="isPublishedPagesListOpen"
      :publishedPagesList="publishedPagesList"
      @close="isPublishedPagesListOpen = false"
      @unpublish="handleUnpublishFromList"
    />

    <!-- 公開ブクマす利用規約モーダル (F-021) -->
    <PublicTermsModal
      :isOpen="isPublicTermsModalOpen"
      :snsUrl="publishSnsUrl"
      :snsPlatform="publishSnsPlatform"
      :snsUsername="publishSnsUsername"
      @accept="acceptPublicTerms"
      @back="goBackFromPublicTerms"
    />
    
    <!-- トースト通知 -->
    <v-snackbar
      v-model="toast.show"
      :timeout="toast.timeout"
      :color="toast.color"
      :location="toast.location"
    >
      {{ toast.text }}
    </v-snackbar>

    <!-- 確認ダイアログ（window.confirm代替・PWAスタンドアロンモード対応） -->
    <v-dialog v-model="confirmDialog.isOpen" max-width="320" persistent>
      <v-card>
        <v-card-title class="text-body-1 font-weight-bold pt-4 px-4">{{ confirmDialog.title }}</v-card-title>
        <v-card-text class="px-4 pb-2" style="white-space: pre-line;">{{ confirmDialog.message }}</v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="onConfirmCancel">キャンセル</v-btn>
          <v-btn :color="confirmDialog.dangerous ? 'error' : 'primary'" variant="elevated" @click="onConfirmOk">{{ confirmDialog.okLabel }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import BookmarkTile from './components/TileFixed.vue';
import draggable from 'vuedraggable';
import BookmarkModal from './components/BookmarkModal.vue';
import PinCodeModal from './components/PinCodeModal.vue';
import UniqueUrlModal from './components/UniqueUrlModal.vue';
import CloudDataModal from './components/CloudDataModal.vue';
import PrivacyPolicyModal from './components/PrivacyPolicyModal.vue';
import AboutPage from './components/AboutPage.vue';
import TermsModal from './components/TermsModal.vue';
import BookmarkPagesModal from './components/BookmarkPagesModal.vue';
import PublishSettingsModal from './components/PublishSettingsModal.vue';
import PublishUrlModal from './components/PublishUrlModal.vue';
import PublishedPagesListModal from './components/PublishedPagesListModal.vue';
import PublicTermsModal from './components/PublicTermsModal.vue';
import logger from './services/logger';
import MailIcon from './assets/icons/MailIcon.vue';
import {
  IconArrowsSort, IconPencil, IconFileExport, IconFileImport,
  IconCloudUpload, IconCloudCheck, IconCloudCog, IconTrash,
  IconFilePlus, IconFiles, IconInfoCircle, IconShieldLock,
  IconBug, IconInfoSmall, IconAlertTriangle, IconAlertCircle, IconEyeOff,
  IconWorld, IconWorldOff, IconLink, IconList
} from '@tabler/icons-vue';
import { getCookie, setCookie, extendAllCookiesOnStartup, clearCloudCookies } from './services/cookieService';
import { createEmptyBookmarks, initializeBookmarks, normalizeBookmarks, saveBookmarksToStorage, saveCustomColorsToStorage, loadCustomColorsFromStorage, getBookmarkStorageKey, migrateBookmarkStorageKey } from './services/bookmarkService';
import { saveToCloud, fetchFromCloud, deleteFromCloud, publishBookmarks, fetchPublishedData, unpublishBookmarks } from './services/cloudService';
export default {
  name: 'App',
  components: {
    BookmarkTile,
    draggable,
    BookmarkModal,
    PinCodeModal,
    UniqueUrlModal,
    CloudDataModal,
    PrivacyPolicyModal,
    AboutPage,
    TermsModal,
    BookmarkPagesModal,
    PublishSettingsModal,
    PublishUrlModal,
    PublishedPagesListModal,
    PublicTermsModal,
    MailIcon,
    // Tabler Icons
    IconArrowsSort, IconPencil, IconFileExport, IconFileImport,
    IconCloudUpload, IconCloudCheck, IconCloudCog, IconTrash,
    IconFilePlus, IconFiles, IconInfoCircle, IconShieldLock,
    IconBug, IconInfoSmall, IconAlertTriangle, IconAlertCircle, IconEyeOff,
    IconWorld, IconWorldOff, IconLink, IconList,
  },
  data() {
    return {
      drawer: false,
      searchQuery: '',
      searchEngine: 'google',
      bookmarks: [], // ブックマーク配列
      customColors: [], // カスタムカラーパレット（最大6色）
      isModalOpen: false,
      currentEditTile: null,
      isDragging: false,
      isMobile: false,
      tileCount: 96,
      
      // クラウド機能関連のデータ
      isPinCodeModalOpen: false,
      isAuthMode: false,      // 認証モード（true：認証、false：新規登録）
      isUniqueUrlModalOpen: false,
      isCloudDataModalOpen: false,
      // クラウド認証用のモーダルフラグ（追加）
      isCloudAuthModalOpen: false,
      pendingCloudAuthId: '', // 認証待ちのクラウドID
      pendingCloudPin: '',    // モーダルで入力されたPINコード（追加）
      uniqueUrl: '',
      cloudSaveLoading: false,
      // クラウド同期関連の状態管理
      isCloudSyncActive: false,   // クラウド同期中かどうかのフラグ
      cloudSyncRetryCount: 0,     // 同期リトライ回数
      maxSyncRetries: 3,          // 最大リトライ回数
      cloudSyncTimer: null,       // 同期遅延タイマー
      lastCloudSyncTime: 0,       // 最後に同期した時間
      // クラウド取得失敗時のオフラインモード管理
      cloudFetchFailed: false,       // エラー画面表示フラグ（ローカルデータなし時）
      isCloudOfflineMode: false,     // オフラインモード（ローカルデータあり時）
      cloudFetchRetrying: false,     // リトライ中フラグ
      cloudFetchRetryInfo: '',       // リトライ状況テキスト
      cloudOfflineRandomId: '',      // オフライン時のクラウドID保持用
      isConflictDialogOpen: false,   // 競合検出ダイアログ表示フラグ
      conflictCloudData: null,       // 競合時のクラウドデータ
      conflictLocalData: null,       // 競合時のローカルデータ
      // プライバシーポリシーモーダル
      isPrivacyPolicyOpen: false,  // プライバシーポリシーモーダルの表示状態
      // AboutPageモーダル
      isAboutPageOpen: false,      // AboutPageモーダルの表示状態
      // 利用規約同意モーダル
      isTermsModalOpen: false,     // 利用規約モーダルの表示状態
      // 確認ダイアログ（window.confirm代替・PWAスタンドアロンモード対応）
      confirmDialog: {
        isOpen: false,
        title: '確認',
        message: '',
        okLabel: 'OK',
        dangerous: false,
        resolve: null
      },

      // トースト通知の設定
      toast: {
        show: false,           // 表示状態
        text: '',              // 表示するテキスト
        timeout: 3000,         // 表示時間（ミリ秒）
        color: 'success',      // 色（success, info, warning, error）
        location: 'bottom'     // 位置
      },
      // 開発環境フラグ
      isDevelopment: process.env.NODE_ENV === 'development',
      
      // スマートフォン操作性改善：モード管理
      currentMode: 'normal', // 'normal', 'sort', 'edit'
      modeLabels: {
        normal: '',
        sort: '並び替え中',
        edit: 'リンク編集中'
      },
      
      // ブックマークページ管理
      isBookmarkPagesModalOpen: false, // ブックマークページ管理モーダルの表示状態
      bookmarkPages: [],               // 保存されたブックマークページ一覧
      currentPageId: '',               // 現在表示中のページID

      // ブックマーク公開機能 (F-021)
      isPublished: false,              // 現在のページの公開状態フラグ
      publishId: '',                   // 現在のページの公開用ID
      publishUrl: '',                  // 現在のページの公開専用URL
      publishedPages: {},              // 公開中の全ページ { cloudId: { publishId, publishUrl, snsUrl } }
      isPublishSettingsOpen: false,    // 公開設定モーダル表示状態
      isPublishUrlModalOpen: false,    // 公開URL通知モーダル表示状態
      isPublishedPagesListOpen: false,  // 公開中ブクマす一覧モーダル表示状態
      isPublicTermsModalOpen: false,   // 公開ブクマス利用規約モーダル表示状態
      isPublicViewMode: false,         // 公開ブクマス閲覧モード（読み取り専用）
      publishSnsUrl: '',               // 公開者のSNS URL
      publishSnsPlatform: '',          // 公開者のSNSプラットフォーム
      publishSnsUsername: ''           // 公開者のSNSユーザー名
    }
  },
  computed: {
    // 公開中のページが1つ以上あるか
    hasAnyPublishedPages() {
      return Object.keys(this.publishedPages).length > 0;
    },
    // 公開閲覧モードのSNSアイコン
    publicViewSnsIcon() {
      const icons = {
        twitter: 'mdi-twitter',
        instagram: 'mdi-instagram',
        facebook: 'mdi-facebook',
        youtube: 'mdi-youtube',
        tiktok: 'mdi-music-note',
        github: 'mdi-github',
        other: 'mdi-account'
      };
      return icons[this.publishSnsPlatform] || 'mdi-account';
    },
    // 公開中のページ一覧（モーダル表示用）
    publishedPagesList() {
      return Object.entries(this.publishedPages).map(([cloudId, info]) => {
        const page = this.bookmarkPages.find(p => p.id === cloudId);
        return {
          cloudId,
          publishId: info.publishId,
          publishUrl: info.publishUrl,
          snsUrl: info.snsUrl || '',
          pageName: (page && page.name) ? page.name : ''
        };
      });
    }
  },
  created() {
    // 最初に強制的にデバイス検出を実行 - ここでセットされたisMobileフラグが各コンポーネントに伝播
    this.isMobile = this.checkDeviceType();
    
    // アプリケーション起動時にcookie期限を延長
    extendAllCookiesOnStartup();
    
    // ブックマークページ一覧を読み込み
    this.loadBookmarkPages();
    
    // カスタムカラーをローカルストレージから復元
    this.customColors = loadCustomColorsFromStorage();

    // リダイレクトフラグ - クラウドからのリダイレクト時にtrueになる
    this.isRedirectedFromCloud = false;

    // 公開ブクマすURLの検出 (/pub/{publishId})
    if (this.checkPublicViewRedirect()) {
      return; // 公開閲覧モードの場合は通常の初期化をスキップ
    }
    
    // クラウドURLへのリダイレクト処理（最後にアクセスしたURLへリダイレクト）
    // リダイレクトする場合は後続の処理をスキップ
    if (this.checkAndRedirectToLastAccessedUrl()) {
      return;
    }
    
    // 利用規約への同意を確認
    this.checkTermsAgreement();
    
    // URLからリダイレクトの確認（クラウドデータが存在する場合）
    // クラウドデータのロードを試みる
    if (this.checkCloudRedirect()) {
      // クラウドデータをロードする場合はフラグを立てる
      this.isRedirectedFromCloud = true;
    } else {
      // クラウドからのリダイレクトでない場合のみローカルデータを初期化
      // デバイスタイプのチェックを強制的に実行（直前にすでに実行しているが念のため）
      this.checkDeviceType();
      window.addEventListener('resize', this.checkDeviceType);

      // ブックマークデータを初期化
      this.bookmarks = initializeBookmarks(this.tileCount, this.getCurrentCloudId());
    }
    
    // クラウド同期状態を確認
    this.checkCloudSyncState();

    // 公開状態を復元
    this.restorePublishState();
    
    // モバイル固有の対策（長押しメニュー表示を優先するため）
    if (this.isMobile) {
      // タッチ画面のハイライト防止
      document.documentElement.style.webkitTapHighlightColor = 'transparent';
      // 文字選択無効化
      document.documentElement.style.userSelect = 'none';
      document.documentElement.style.webkitUserSelect = 'none';
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkDeviceType);
  },
  methods: {
    // スマートフォン操作性改善：モード切替メソッド
    setMode(mode) {
      this.currentMode = mode;
      
      // モード切替時にタイルの状態をリフレッシュ
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    
    // トースト通知を表示するメソッド
    showToast(text, color = 'success', timeout = 3000) {
      this.toast.text = text;
      this.toast.color = color;
      this.toast.timeout = timeout;
      this.toast.show = true;
    },
    
    exportData() {
      // ブックマークデータをJSON形式でエクスポート（カスタムカラー含む）
      const exportObj = { bookmarks: this.bookmarks, customColors: this.customColors };
      const dataStr = JSON.stringify(exportObj);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      // ダウンロードリンクを作成して自動クリック
      const exportFileName = 'bookmarks_export_' + new Date().toISOString().slice(0,10) + '.json';
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileName);
      linkElement.click();
    },
    showImportDialog() {
      // ファイル選択ダイアログを表示
      this.$refs.fileInput.click();
    },
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          
          // 新形式（オブジェクト: { bookmarks, customColors }）と旧形式（配列）の両方を処理
          let bookmarksArr;
          let colorsArr = [];
          if (Array.isArray(importedData) && importedData.length > 0) {
            bookmarksArr = importedData;
          } else if (importedData && Array.isArray(importedData.bookmarks) && importedData.bookmarks.length > 0) {
            bookmarksArr = importedData.bookmarks;
            colorsArr = importedData.customColors || [];
          }
          
          if (bookmarksArr) {
            // 【型安全性強化】すべてのidを文字列型に変換してから処理
            this.bookmarks = normalizeBookmarks(bookmarksArr);
            saveBookmarksToStorage(this.bookmarks, this.getCurrentCloudId());
            
            // カスタムカラーも復元（空の場合はデフォルト値を使用）
            if (Array.isArray(colorsArr) && colorsArr.length > 0) {
              this.customColors = colorsArr;
              saveCustomColorsToStorage(colorsArr);
            }
            
            // オフライン中の変更を記録
            this.markOfflineModified();
            // クラウド同期が有効な場合は、インポートデータを即座にクラウドへ同期
            if (this.isCloudSyncActive) {
              this.syncToCloud();
            }
            
            this.showToast('ブックマークデータをインポートしました', 'success');
          } else {
            this.showToast('インポートしたファイルの形式が正しくありません', 'error');
          }
        } catch (error) {
          console.error('インポートエラー:', error);
          this.showToast('ファイルの解析に失敗しました: ' + error.message, 'error');
        }
      };
      reader.readAsText(file);
      
      // ファイル入力をリセット（同じファイルを再度選択できるように）
      event.target.value = null;
    },
    async resetAllTiles() {
      const confirmed = await this.showConfirm('すべてのブックマークを削除しますか？この操作は元に戻せません。', { title: '全件削除の確認', okLabel: '全件削除', dangerous: true });
      if (confirmed) {
        this.bookmarks = createEmptyBookmarks(this.tileCount);
        saveBookmarksToStorage(this.bookmarks, this.getCurrentCloudId());
        
        // オフライン中の変更を記録
        this.markOfflineModified();
        // クラウド同期が有効な場合は、リセットデータを即座にクラウドへ同期
        if (this.isCloudSyncActive) {
          this.syncToCloud();
        }
        
        this.showToast('すべてのブックマークがリセットされました', 'success');
      }
    },
    showAboutPage() {
      this.isAboutPageOpen = true;
    },
    executeSearch() {
      if (!this.searchQuery.trim()) {
        return;
      }
      
      let searchUrl = '';
      
      switch (this.searchEngine) {
        case 'google':
          searchUrl = `https://www.google.com/search?q=${encodeURIComponent(this.searchQuery)}`;
          break;
        case 'bing':
          searchUrl = `https://www.bing.com/search?q=${encodeURIComponent(this.searchQuery)}`;
          break;
        case 'chatgpt':
          searchUrl = `https://chat.openai.com/?q=${encodeURIComponent(this.searchQuery)}`;
          break;
        default:
          searchUrl = `https://www.google.com/search?q=${encodeURIComponent(this.searchQuery)}`;
      }
      
      window.open(searchUrl, '_blank');
    },
    // Macドック風拡大アニメーション - マウス移動時
    handleDockMouseMove(event) {
      if (this.isMobile || this.isDragging) return;
      if (this._dockRafId) return;

      const mouseX = event.clientX;
      const mouseY = event.clientY;
      const container = event.currentTarget;

      this._dockRafId = requestAnimationFrame(() => {
        this._dockRafId = null;
        if (!container) return;

        const tiles = container.querySelectorAll('.tile');
        const maxDistance = 160;
        const maxScale = 1.25;

        tiles.forEach(tile => {
          const rect = tile.getBoundingClientRect();
          const tileCenterX = rect.left + rect.width / 2;
          const tileCenterY = rect.top + rect.height / 2;
          const distance = Math.hypot(mouseX - tileCenterX, mouseY - tileCenterY);

          if (distance < maxDistance) {
            const ratio = 1 - distance / maxDistance;
            // cosカーブで自然な拡大（Dock風）
            const scale = 1 + (maxScale - 1) * Math.cos((1 - ratio) * Math.PI / 2);
            tile.style.transform = `scale(${scale})`;
            tile.style.zIndex = Math.round(scale * 100).toString();
          } else {
            tile.style.transform = '';
            tile.style.zIndex = '';
          }
        });
      });
    },
    // Macドック風拡大アニメーション - マウス離脱時
    handleDockMouseLeave() {
      if (this.isMobile) return;
      if (this._dockRafId) {
        cancelAnimationFrame(this._dockRafId);
        this._dockRafId = null;
      }
      const wrapper = this.$el.querySelector('.tiles-container-wrapper');
      if (!wrapper) return;
      const tiles = wrapper.querySelectorAll('.tile');
      tiles.forEach(tile => {
        tile.style.transform = '';
        tile.style.zIndex = '';
      });
    },
    // ドラッグ開始時の処理
    onDragStart() {
      this.isDragging = true;
      // ドラッグ中はドック拡大をリセット
      this.handleDockMouseLeave();
    },
    // ドラッグ終了時の処理
    onDragEnd() {
      this.isDragging = false;
      // ブックマークの順序をローカルストレージに保存
      this.saveBookmarksOrder();
    },
    // タイルクリック時の処理
    handleTileClick(tile) {
      // イベント追跡用のタイムスタンプを保存（重複クリック検出用）
      const clickTime = Date.now();
      const lastClickTime = this._lastClickTime || 0;
      this._lastClickTime = clickTime;
      
      // 既に処理済みのクリックイベントかチェック
      const processedClicks = this._processedClicks || [];
      if (tile.timestamp && processedClicks.includes(tile.timestamp)) {
        return;
      }
      
      // 処理済みイベントとして記録（最大10個まで保存）
      if (tile.timestamp) {
        processedClicks.push(tile.timestamp);
        if (processedClicks.length > 10) processedClicks.shift();
        this._processedClicks = processedClicks;
      }

      // 連続クリック防止（100ms以内の連続クリックを無視）
      if (clickTime - lastClickTime < 100) {
        return;
      }

      
      // 空のタイルの場合は編集モーダルを表示
      if (tile.isEmpty) {
        this.editTileContent(tile);
        return;
      }
      
      // 編集モード時はリンクを開かずに編集モーダルを表示
      if (this.currentMode === 'edit') {
        this.editTileContent(tile);
        return;
      }
      
      // URL無しや、ドラッグ中はスキップ
      if (!tile.url || this.isDragging) {
        return;
      }
      
      // 同じURLの連続オープンを防止（2秒以内）
      const now = Date.now();
      if (tile.url === this._lastOpenedUrl && now - (this._lastOpenedTime || 0) < 2000) {
        return;
      }
      this._lastOpenedUrl = tile.url;
      this._lastOpenedTime = now;

      // タイルからのクリックイベントでURLを開く
      try {
        window.open(tile.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('URLを開く際にエラーが発生しました:', error);
        this.showToast('URLを開けませんでした: ' + error.message, 'error');
      }
    },
    // 編集モーダルを表示
    editTileContent(tile) {
      this.currentEditTile = tile;
      this.isModalOpen = true;
    },
  // タイルの内容をクリア
  async clearTileContent(tile) {
    console.log('[App.vue] clearTileContent called with tile:', tile);
    
    // 削除前に確認ダイアログを表示（PWAスタンドアロン対応のインアプリダイアログ）
    const message = `${tile.url || '(URL未設定)'}\nこのブックマークを削除します`;
    const confirmed = await this.showConfirm(message, { title: '削除の確認', okLabel: '削除', dangerous: true });
    if (confirmed) {
      console.log('[App.vue] User confirmed deletion');
      this.deleteTileWithoutConfirm(tile);
    } else {
      console.log('[App.vue] User cancelled deletion');
    }
  },

  // 確認ダイアログなしでタイルを削除（モーダルからの削除用）
  deleteTileWithoutConfirm(tile) {
    // 【デグレ防止】削除対象の詳細検証
    if (!tile) {
      console.error('[App.vue] 削除失敗: tileオブジェクトがnullです');
      this.showToast('エラー: 削除対象のタイル情報が見つかりません。', 'error');
      return;
    }
    
    // IDを文字列に正規化（数値型の場合も対応）
    const tileId = String(tile.id || '').trim();
    
    if (!tileId) {
      console.error('[App.vue] 削除失敗: タイルIDが無効です:', {
        tile,
        idType: typeof tile.id,
        idValue: tile.id,
        normalizedId: tileId
      });
      this.showToast('エラー: タイルIDが無効なため削除できません。ページを再読み込みしてやり直してください。', 'error');
      return;
    }
    
    const index = this.bookmarks.findIndex(b => String(b.id) === tileId);
    
    if (index === -1) {
      console.error('[App.vue] 削除失敗: 指定されたIDのタイルが見つかりません:', {
        targetId: tile.id,
        normalizedTargetId: tileId,
        availableIds: this.bookmarks.map(b => ({ original: b.id, normalized: String(b.id) })),
        bookmarksLength: this.bookmarks.length
      });
      this.showToast('エラー: 削除対象のタイルが見つかりません。ページを再読み込みしてやり直してください。', 'error');
      return;
    }
    
    console.log('[App.vue] Before deletion:', this.bookmarks[index]);
    
    // Vueのリアクティビティを確保するため、スプレッド演算子で新しいオブジェクトを作成
    this.bookmarks[index] = {
      ...this.bookmarks[index],  // 元のオブジェクトのプロパティを維持
      id: this.bookmarks[index].id,  // 元のIDを維持（数値型か文字列型かに関わらず）
      name: '',                  // 名前をクリア
      url: '',                   // URLをクリア
      iconUrl: '',               // アイコンURLをクリア
      color: '',                 // 色をクリア
      customIcon: '',            // カスタムアイコンをクリア
      isEmpty: true              // 空フラグを設定
    };
    
    console.log('[App.vue] After deletion:', this.bookmarks[index]);
    
    // リアクティビティを確保するためにbookmarks配列自体も更新
    this.bookmarks = [...this.bookmarks];
    
    // ローカルストレージに保存
    try {
      saveBookmarksToStorage(this.bookmarks, this.getCurrentCloudId());
      console.log('[App.vue] Saved to localStorage successfully');
    } catch (error) {
      console.error('[App.vue] ローカルストレージ保存エラー:', error);
      this.showToast('警告: ローカルストレージへの保存に失敗しました。ブラウザの容量を確認してください。', 'warning');
    }
    
    // オフライン中の変更を記録
    this.markOfflineModified();
    // クラウド同期が有効な場合は、クラウドにも保存
    if (this.isCloudSyncActive) {
      console.log('[App.vue] Syncing to cloud');
      this.syncToCloud();
    }
  },
    // ブックマーク保存時の処理
    saveBookmark(bookmarkData) {
      if (!this.currentEditTile) return;
      
      const index = this.bookmarks.findIndex(b => b.id === this.currentEditTile.id);
      if (index !== -1) {
        this.bookmarks[index] = {
          ...this.bookmarks[index],
          name: bookmarkData.name,
          url: bookmarkData.url,
          iconUrl: bookmarkData.iconUrl,
          color: bookmarkData.color,
          customIcon: bookmarkData.customIcon || '',
          isEmpty: false
        };
        
        // ローカルストレージに保存
        saveBookmarksToStorage(this.bookmarks, this.getCurrentCloudId());
        
        // オフライン中の変更を記録
        this.markOfflineModified();
        // クラウド同期が有効な場合は、クラウドにも保存
        if (this.isCloudSyncActive) {
          this.syncToCloud();
        }
      }
      
      // モーダルを閉じる
      this.isModalOpen = false;
      this.currentEditTile = null;
    },

    // カスタムカラーパレット更新
    updateCustomColors(colors) {
      this.customColors = colors;
      saveCustomColorsToStorage(colors);
      // オフライン中の変更を記録
      this.markOfflineModified();
      // クラウド同期が有効な場合は同期
      if (this.isCloudSyncActive) {
        this.syncToCloud();
      }
    },

    // モーダルからの削除処理
    async deleteBookmarkFromModal(tile) {
      console.log('[App.vue] deleteBookmarkFromModal called with tile:', tile);
      
      const confirmed = await this.showConfirm(`「${tile.name}」を削除しますか？`, { title: '削除の確認', okLabel: '削除', dangerous: true });
      if (confirmed) {
        console.log('[App.vue] User confirmed modal deletion');
        this.deleteTileWithoutConfirm(tile);
        this.isModalOpen = false;
        this.currentEditTile = null;
      } else {
        console.log('[App.vue] User cancelled modal deletion');
      }
    },
    
    // クラウドへの同期を行うメソッド
    syncToCloud() {
      // すでに同期処理が進行中の場合は新たなリクエストをキュー
      if (this.cloudSyncTimer) {
        clearTimeout(this.cloudSyncTimer);
      }
      
      // 同期対象のデータをスケジュール時点でキャプチャ（ページ切り替え競合を防止）
      const capturedCloudId = localStorage.getItem('bkms_cloud_id');
      const capturedBookmarks = JSON.parse(JSON.stringify(this.bookmarks));
      const capturedCustomColors = JSON.parse(JSON.stringify(this.customColors));
      
      // 短時間に複数の変更がある場合を考慮して、遅延実行する
      this.cloudSyncTimer = setTimeout(() => {
        // 最後の同期から間もない場合は処理をスキップ（連続同期防止）
        const now = Date.now();
        const timeSinceLastSync = now - this.lastCloudSyncTime;
        const MIN_SYNC_INTERVAL_MS = 5000; // 最低5秒間隔をあける
        
        if (timeSinceLastSync < MIN_SYNC_INTERVAL_MS) {
          return;
        }
        
        // クラウド同期のための情報を取得
        const cloudUrl = getCookie('bkms_cloud_url');
        const isAuthenticated = getCookie('bkms_cloud_auth') === 'true';
        const pinCode = getCookie('bkms_cloud_pin');
        
        // キャプチャ時点のcloudIdを使用（遅延中にページが切り替わっても安全）
        if (!cloudUrl || !isAuthenticated || !capturedCloudId || !pinCode) {
          console.error('クラウド同期に必要な情報が不足しています');
          return;
        }
        
        // ページ切り替え後に旧ページのデータが送信されることを防止
        const currentCloudId = localStorage.getItem('bkms_cloud_id');
        if (currentCloudId !== capturedCloudId) {
          console.warn('[syncToCloud] ページが切り替わったため同期をスキップ (captured:', capturedCloudId, 'current:', currentCloudId, ')');
          return;
        }
        
        // cloudService を使って Cloudflare KV にデータを保存する
        const cloudData = { bookmarks: capturedBookmarks, customColors: capturedCustomColors };
        saveToCloud(capturedCloudId, cloudData, pinCode)
        .then(() => {
          // 最後の同期時間を更新
          this.lastCloudSyncTime = Date.now();
          
          // クラウド保存日時をローカルストレージに記録
          localStorage.setItem('bkms_cloud_saved_at', new Date().toISOString());
          
          // 同期リトライカウンタをリセット
          this.cloudSyncRetryCount = 0;
        })
        .catch(error => {
          console.error('クラウド同期エラー:', error);
          
          // リトライ回数をインクリメント
          this.cloudSyncRetryCount++;
          
          // 最大リトライ回数未満であれば再試行
          if (this.cloudSyncRetryCount < this.maxSyncRetries) {
            setTimeout(() => this.syncToCloud(), 3000 * this.cloudSyncRetryCount); // 徐々に間隔を広げる
          } else {
            console.error(`最大リトライ回数(${this.maxSyncRetries}回)に達しました。同期を中断します`);
            this.cloudSyncRetryCount = 0; // リセット
          }
        })
        .finally(() => {
          this.cloudSyncTimer = null;
        });
      }, 1000); // 1秒の遅延を設ける
    },
    
    // オフラインモード中のローカル変更を記録
    markOfflineModified() {
      if (this.isCloudOfflineMode) {
        localStorage.setItem('bkms_offline_modified', 'true');
      }
    },
    
    // クラウドデータ取得の手動リトライ（エラー画面の「再試行」ボタン用）
    retryCloudFetch() {
      if (!this.cloudOfflineRandomId) return;
      this.cloudFetchFailed = false;
      this.isCloudOfflineMode = false;
      this.fetchCloudData(this.cloudOfflineRandomId);
    },
    
    // エラー画面からトップに戻る（URLは保持したまま）
    goToHome() {
      this.cloudFetchFailed = false;
      window.location.href = window.location.origin;
    },
    
    // 競合解決: クラウドデータを採用
    resolveConflictWithCloud() {
      const randomId = this.cloudOfflineRandomId;
      if (this.conflictCloudData) {
        saveBookmarksToStorage(this.conflictCloudData, randomId);
        this.bookmarks = this.conflictCloudData;
        this.showToast('クラウドデータを適用しました', 'success');
      }
      this.finalizeConflictResolution(randomId);
    },
    
    // 競合解決: ローカルデータを採用
    resolveConflictWithLocal() {
      const randomId = this.cloudOfflineRandomId;
      if (this.conflictLocalData) {
        saveBookmarksToStorage(this.conflictLocalData, randomId);
        this.bookmarks = this.conflictLocalData;
        // ローカルデータを正としてクラウドに上書き同期
        this.isCloudSyncActive = true;
        this.syncToCloud();
        this.showToast('ローカルデータを適用し、クラウドに同期します', 'success');
      }
      this.finalizeConflictResolution(randomId);
    },
    
    // 競合解決の共通後処理
    finalizeConflictResolution(randomId) {
      this.isConflictDialogOpen = false;
      this.conflictCloudData = null;
      this.conflictLocalData = null;
      this.isCloudSyncActive = true;
      this.isCloudOfflineMode = false;
      localStorage.removeItem('bkms_offline_modified');
      localStorage.removeItem('bkms_offline_since');
      localStorage.setItem('bkms_cloud_id', randomId);
    },

    // 確認ダイアログを表示してPromiseを返す（window.confirm代替・PWAスタンドアロン対応）
    showConfirm(message, { title = '確認', okLabel = 'OK', dangerous = false } = {}) {
      return new Promise((resolve) => {
        this.confirmDialog.title = title;
        this.confirmDialog.message = message;
        this.confirmDialog.okLabel = okLabel;
        this.confirmDialog.dangerous = dangerous;
        this.confirmDialog.resolve = resolve;
        this.confirmDialog.isOpen = true;
      });
    },
    onConfirmOk() {
      this.confirmDialog.isOpen = false;
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(true);
        this.confirmDialog.resolve = null;
      }
    },
    onConfirmCancel() {
      this.confirmDialog.isOpen = false;
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(false);
        this.confirmDialog.resolve = null;
      }
    },

    // モーダルを閉じる
    closeModal() {
      this.isModalOpen = false;
      this.currentEditTile = null;
    },
    // ブックマークの順序をローカルストレージに保存
    saveBookmarksOrder() {
      saveBookmarksToStorage(this.bookmarks, this.getCurrentCloudId());
      
      // オフライン中の変更を記録
      this.markOfflineModified();
      // クラウドURL情報が存在する場合、クラウドにも保存
      const cloudUrl = getCookie('bkms_cloud_url');
      const isAuthenticated = getCookie('bkms_cloud_auth') === 'true';
      
      if (cloudUrl && isAuthenticated) {
        this.scheduleCloudSync();
      }
    },
    // デバイスのタイプをチェックして適切にisMobileフラグを設定（改良版）
    checkDeviceType() {
      // UA文字列でモバイルデバイスを検出
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i.test(userAgent);
      
      // 画面サイズによる検出
      const screenWidth = window.innerWidth;
      const tabletMinWidth = 740;
      const isMobileSize = screenWidth < tabletMinWidth;
      
      // PCモードの強制判定用URL検出
      const forceDesktopMode = window.location.search.indexOf('desktop=true') !== -1;
      
      // PCモード強制パラメータがある場合はモバイル判定をオーバーライド
      if (forceDesktopMode) {
        this.isMobile = false;
      } else {
        // 複合的な条件でモバイル判定（修正版）
        // スマホ確定の場合のみモバイル判定する
        // タブレットやタッチ対応PCではドラッグを可能にするためPCモードを維持
        this.isMobile = isMobileDevice && isMobileSize;
      }
      
      // 【デバッグ】デバイス判定の詳細ログ
      console.log('[App.vue] Device detection:', {
        userAgent: userAgent.substring(0, 100),
        isMobileDevice,
        screenWidth,
        isMobileSize,
        forceDesktopMode,
        finalIsMobile: this.isMobile
      });
      
      // グローバルフラグとして document と body の両方に データ属性を設定
      document.documentElement.setAttribute('data-is-mobile', this.isMobile);
      document.body.setAttribute('data-is-mobile', this.isMobile);
      
      return this.isMobile;
    },
    // 最後にアクセスしたURLにリダイレクトする処理（複数ページ対応）
    checkAndRedirectToLastAccessedUrl() {
      // トップページにアクセスされた場合のみリダイレクトを検討
      if (window.location.pathname === '/') {
        // 新しいページ作成中はリダイレクトをスキップ
        if (sessionStorage.getItem('bkms_creating_new_page') === 'true') {
          sessionStorage.removeItem('bkms_creating_new_page');
          return false;
        }

        // リダイレクトループ検出: 前回のリダイレクト先が存在しないページだった場合
        const lastRedirectUrl = sessionStorage.getItem('bkms_redirect_target_url');
        if (lastRedirectUrl) {
          sessionStorage.removeItem('bkms_redirect_target_url');
          // 同じURLに戻ってきた = ページが存在しない → 一覧から削除
          const failedPage = this.bookmarkPages.find(p => p.url === lastRedirectUrl);
          if (failedPage) {
            this.bookmarkPages = this.bookmarkPages.filter(p => p.url !== lastRedirectUrl);
            this.saveBookmarkPages();
            this.showToast(`「${failedPage.name || 'ブクマす'}」はクラウドに存在しないため一覧から削除しました`, 'info', 4000);
          }
          return false;
        }

        // ブックマークページ一覧から最後にアクセスしたページを取得
        const lastAccessedPage = this.getLastAccessedPage();
        if (lastAccessedPage && lastAccessedPage.url && lastAccessedPage.url !== window.location.href) {
          sessionStorage.setItem('bkms_redirect_target_url', lastAccessedPage.url);
          window.location.href = lastAccessedPage.url;
          return true;
        }
        // フォールバック: 従来のbkms_cloud_url cookieも確認
        const cloudUrl = getCookie('bkms_cloud_url');
        if (cloudUrl && cloudUrl !== window.location.href) {
          sessionStorage.setItem('bkms_redirect_target_url', cloudUrl);
          window.location.href = cloudUrl;
          return true;
        }
      }
      return false;
    },

    // クラウドリダイレクトをチェック
    checkCloudRedirect() {
      // URLからランダムIDを取得
      const path = window.location.pathname;
      // /pub/ パスは公開閲覧用なのでクラウドリダイレクト対象外
      if (path.startsWith('/pub/')) return false;
      const randomId = path.length > 1 ? path.substring(1) : ''; // 最初の'/'を除外
      
      if (randomId) {
        try {
          const fullUrl = window.location.origin + path;
          
          // 既存のクラウドURLが異なる場合、先にページ一覧に登録して保全
          const previousCloudUrl = getCookie('bkms_cloud_url');
          if (previousCloudUrl && previousCloudUrl !== fullUrl) {
            try {
              const prevPath = new URL(previousCloudUrl).pathname;
              const prevId = prevPath.length > 1 ? prevPath.substring(1) : '';
              if (prevId) {
                // 旧ページのデータをページ別キーにマイグレーション
                migrateBookmarkStorageKey(prevId);
                this.registerCurrentPage(prevId, previousCloudUrl);
              }
            } catch (e) {
              console.warn('[App.vue] 既存クラウドURLの解析に失敗:', e);
            }
          }
          
          // ランダムIDが存在する場合、それをcookieに保存
          setCookie('bkms_cloud_url', fullUrl, 365); // 1年間有効
          
          // 新しいページのストレージキーをマイグレーション（旧形式から新形式へ）
          migrateBookmarkStorageKey(randomId);
          
          // ブックマークページとして登録（複数ページ対応）
          this.registerCurrentPage(randomId, fullUrl);
          
          // クラウドURLからのアクセスを記録（利用規約同意後の処理で使用）
          localStorage.setItem('bkms_pending_cloud_id', randomId);
          
          // まず利用規約への同意確認を行う
          // cookieから同意状態を確認
          const hasAgreed = getCookie('bkms_terms_agreed') === 'true';
          // ローカルストレージからも確認（バックアップ）
          const localStorageAgreement = localStorage.getItem('bkms_terms_agreed') === 'true';
          
          if (hasAgreed || localStorageAgreement) {
            // 既に同意済みの場合はPIN認証へ進む
            this.proceedToCloudAuthentication(randomId);
          } else {
            // 未同意の場合は利用規約を表示
            this.isTermsModalOpen = true;
          }
        } catch (error) {
          console.error('[App.vue] checkCloudRedirect でエラー:', error);
        }
        
        return true; // クラウドリダイレクトを検出
      }
      
      return false; // クラウドリダイレクトなし
    },

    // クラウド認証処理（利用規約同意後に実行）
    proceedToCloudAuthentication(randomId) {
      // ロックアウト状態チェック（リロード対策）
      const lockoutUntil = localStorage.getItem('bkms_pin_lockout_until');
      if (lockoutUntil && Date.now() < Number(lockoutUntil)) {
        const remainMin = Math.ceil((Number(lockoutUntil) - Date.now()) / 60000);
        this.showToast(`PIN認証がロック中です。${remainMin}分後に再試行してください。`, 'error');
        this.isCloudSyncActive = false;
        return;
      }
      // ロックアウト期限切れなら削除
      if (lockoutUntil) {
        localStorage.removeItem('bkms_pin_lockout_until');
      }

      // 認証ステータスをチェック
      const isAuthenticated = getCookie('bkms_cloud_auth') === 'true';
      
      if (!isAuthenticated) {
        // 未認証の場合はPINコード入力モーダルを表示
        this.showCloudAuthDialog(randomId);
      } else {
        // 認証済みの場合はデータを取得
        this.fetchCloudData(randomId);
      }
    },

    // クラウド認証ダイアログを表示
    showCloudAuthDialog(randomId) {
      // アラート/プロンプトからPINコードモーダルに変更
      this.pendingCloudAuthId = randomId; // 認証後に処理するIDを保存
      this.isAuthMode = true; // 認証モード
      this.isPinCodeModalOpen = true; // PINコード入力モーダルを表示
    },

    // ブックマークデータを比較する
    compareBookmarkData(localData, cloudData) {
      if (!localData || !cloudData) return false;
      if (localData.length !== cloudData.length) return false;
      
      // 各ブックマークを比較
      for (let i = 0; i < localData.length; i++) {
        const local = localData[i];
        const cloud = cloudData[i];
        
        // 重要なフィールドを比較
        if (local.name !== cloud.name || 
            local.url !== cloud.url || 
            local.iconUrl !== cloud.iconUrl ||
            local.color !== cloud.color ||
            local.isEmpty !== cloud.isEmpty) {
          return false;
        }
      }
      return true;
    },

    // クラウドからデータを取得
    fetchCloudData(randomId) {
      // ローカル開発環境とCloudflare KV環境の両方に対応するようにする
      this.cloudSaveLoading = true;
      // オフラインモード状態をリセット
      this.cloudFetchFailed = false;
      this.isCloudOfflineMode = false;
      this.cloudFetchRetrying = false;
      this.cloudFetchRetryInfo = '';
      
      // まず、ローカルデータが存在する場合は即座に表示（高速化）
      const storageKey = getBookmarkStorageKey(randomId);
      const localData = localStorage.getItem(storageKey) || localStorage.getItem('bookmarks');
      let localBookmarks = null;
      const hasLocalData = !!localData;
      
      if (localData) {
        try {
          const parsedData = JSON.parse(localData);
          localBookmarks = parsedData.map((bookmark, index) => ({
            ...bookmark,
            id: bookmark.id ? String(bookmark.id) : `tile-${index + 1}`,
          }));
          
          // ローカルデータをまず表示
          this.bookmarks = localBookmarks;
          
          // クラウド同期状態を有効に設定
          this.isCloudSyncActive = true;
        } catch (e) {
          console.error('ローカルストレージのデータパースに失敗しました:', e);
          localBookmarks = null;
          // 破損したデータを削除
          localStorage.removeItem(storageKey);
        }
      }
      
      // ローカルデータがない場合は空のデータセットを作成
      if (!localBookmarks) {
        localBookmarks = createEmptyBookmarks(this.tileCount);
        this.bookmarks = localBookmarks;
      }
      
      // 開発環境かどうかを判定
      const isLocalDev = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' || 
                         window.location.hostname.startsWith('10.0.1.') ||
                         window.location.hostname.includes('.vscode-webview.');
                         
      // 処理開始
      setTimeout(() => {
        try {
          if (isLocalDev) {
            // ローカル開発環境では、既にローカルデータを表示済みなので
            // クラウドデータの比較は行わない（ローカル開発では同じデータ）
            this.isCloudSyncActive = true;
            sessionStorage.removeItem('bkms_redirect_target_url');
          } else {
            // オフライン中のローカル変更があったかチェック
            const wasOfflineModified = localStorage.getItem('bkms_offline_modified') === 'true';
            
            // リトライ付きでクラウドデータ取得を実行
            const RETRY_MAX = hasLocalData ? 0 : 3; // ローカルデータなし時のみ自動リトライ
            const RETRY_DELAYS = [3000, 5000, 8000];
            let retryCount = 0;
            
            const attemptFetch = () => {
              if (retryCount > 0) {
                this.cloudFetchRetrying = true;
                this.cloudFetchRetryInfo = `クラウドデータ取得を再試行中... (${retryCount}/${RETRY_MAX})`;
                this.showToast(this.cloudFetchRetryInfo, 'warning');
              }
              
              return this.fetchCloudDataFromAPI(randomId)
                .catch(error => {
                  const isTemporary = error.isNetworkError || error.isRetryable;
                  
                  // 一時エラーでない場合（認証エラー、データ不正等）はリトライしない
                  if (!isTemporary) throw error;
                  
                  retryCount++;
                  if (retryCount <= RETRY_MAX) {
                    return new Promise((resolve, reject) => {
                      setTimeout(() => {
                        attemptFetch().then(resolve).catch(reject);
                      }, RETRY_DELAYS[retryCount - 1]);
                    });
                  }
                  throw error; // リトライ上限到達
                });
            };
            
            attemptFetch()
              .then(cloudData => {
                this.cloudFetchRetrying = false;
                this.cloudFetchRetryInfo = '';
                
                // IDを正規化
                const normalizedCloudData = cloudData.map((bookmark, index) => ({
                  ...bookmark,
                  id: bookmark.id ? String(bookmark.id) : `tile-${index + 1}`,
                }));
                
                // オフライン中にローカル変更があった場合 → 競合検出
                if (wasOfflineModified && hasLocalData) {
                  const isDataSame = this.compareBookmarkData(localBookmarks, normalizedCloudData);
                  if (!isDataSame) {
                    // 競合検出: ユーザーに選択させる
                    this.conflictCloudData = normalizedCloudData;
                    this.conflictLocalData = [...this.bookmarks];
                    this.cloudOfflineRandomId = randomId;
                    this.isConflictDialogOpen = true;
                    // オフライン変更フラグはダイアログ解決時にクリア
                    // 同期は一旦停止したまま
                    this.isCloudSyncActive = false;
                    localStorage.setItem('bkms_cloud_id', randomId);
                    sessionStorage.removeItem('bkms_redirect_target_url');
                    return;
                  }
                }
                
                // オフライン変更フラグをクリア（競合なし or 変更なし）
                localStorage.removeItem('bkms_offline_modified');
                localStorage.removeItem('bkms_offline_since');
                
                // ローカルデータとクラウドデータを比較
                const isDataSame = this.compareBookmarkData(localBookmarks, normalizedCloudData);
                
                if (!isDataSame) {
                  saveBookmarksToStorage(normalizedCloudData, randomId);
                  this.bookmarks = normalizedCloudData;
                  this.showToast('クラウドデータ読み込み：ブックマークデータを読み込みました', 'success');
                }
                
                // クラウド同期状態を有効に設定
                this.isCloudSyncActive = true;
                this.isCloudOfflineMode = false;
                
                sessionStorage.removeItem('bkms_redirect_target_url');
                localStorage.setItem('bkms_cloud_id', randomId);
              })
              .catch(error => {
                console.error('クラウドデータ取得エラー:', error);
                this.cloudFetchRetrying = false;
                this.cloudFetchRetryInfo = '';
                
                const isTemporary = error.isNetworkError || error.isRetryable;
                
                if (isTemporary) {
                  // === 一時エラー: URL/PINを保持 ===
                  if (hasLocalData) {
                    // 【ローカルデータあり】オフラインモード
                    this.isCloudOfflineMode = true;
                    this.cloudOfflineRandomId = randomId;
                    this.isCloudSyncActive = false; // 同期を停止（古いデータでクラウド上書き防止）
                    localStorage.setItem('bkms_offline_since', new Date().toISOString());
                    localStorage.setItem('bkms_cloud_id', randomId);
                    this.showToast('ネットワークエラーのため、ローカルデータを表示しています。クラウド同期は一時停止中です。', 'warning');
                  } else {
                    // 【ローカルデータなし】エラー画面表示（編集不可）
                    this.cloudFetchFailed = true;
                    this.cloudOfflineRandomId = randomId;
                    this.isCloudSyncActive = false;
                    localStorage.setItem('bkms_cloud_id', randomId);
                  }
                  // URL/PIN Cookieは保持（クリアしない）
                } else if (error.isAuthError) {
                  if (error.isLocked) {
                    // === ロックアウト: PIN再入力を許可しない ===
                    const retrySeconds = error.retryAfterSeconds || 600;
                    const minutes = Math.ceil(retrySeconds / 60);
                    // ロックアウト状態をlocalStorageに保存（リロード対策）
                    const lockoutUntil = Date.now() + retrySeconds * 1000;
                    localStorage.setItem('bkms_pin_lockout_until', String(lockoutUntil));
                    // 認証Cookieをクリア（リロード時に自動再試行させない）
                    clearCloudCookies();
                    this.showToast(`PIN認証の試行回数上限に達しました。${minutes}分後に再試行してください。`, 'error');
                    this.isCloudSyncActive = false;
                  } else {
                    // === 認証エラー: PIN再入力を促す ===
                    let msg = '認証エラーです。PINコードを再入力してください。';
                    if (error.attemptCount && error.attemptCount >= 2) {
                      msg += `（${error.attemptCount}/${error.maxAttempts}回目）`;
                    }
                    this.showToast(msg, 'error');
                    this.showCloudAuthDialog(randomId);
                  }
                } else {
                  // === 永続エラー (404等): 従来通りクリア+リダイレクト ===
                  this.showToast('クラウドからのデータ取得に失敗しました。', 'error');
                  this.isCloudSyncActive = false;
                  clearCloudCookies();
                  setTimeout(() => {
                    window.location.href = window.location.origin;
                  }, 1500);
                }
              });
          }
          
          // 最終アクセス日時を更新
          const now = new Date().toISOString();
          localStorage.setItem('bkms_last_cloud_access', now);
        } catch (error) {
          console.error('クラウドデータの取得に失敗しました:', error);
          this.showToast('データの取得に失敗しました。再試行するか、新しいブックマークを作成してください。', 'error');
          
          this.bookmarks = initializeBookmarks(this.tileCount, randomId);
          this.isCloudSyncActive = false;
          clearCloudCookies();
          setTimeout(() => {
            window.location.href = window.location.origin;
          }, 1500);
        } finally {
          // ローディング状態を解除
          this.cloudSaveLoading = false;
        }
      }, 1000);
    },

    // Cloudflare KVからデータを取得する実際のAPI呼び出し
    async fetchCloudDataFromAPI(randomId) {
      try {
        // Cookieに保存されているPINコードを取得（設定済みの場合）
        const savedPin = getCookie('bkms_cloud_pin');
        
        // 認証済みのPINコードを使用するか、pendingCloudAuthIdから取得（二重認証の防止）
        let pinCode = savedPin;
        
        // 認証待ちIDとリクエストIDが一致する場合（PINコードモーダルからの処理）
        if (this.pendingCloudAuthId === randomId && this.pendingCloudPin) {
          pinCode = this.pendingCloudPin;
          this.pendingCloudPin = ''; // 使用後はクリア
          this.pendingCloudAuthId = ''; // 使用後はクリア
        }
        
        // PINコードが取得できない場合はエラー
        if (!pinCode) {
          throw new Error('PINコードが取得できませんでした');
        }
        
        const pinCodeStr = String(pinCode);
        
        // cloudService を使って Cloudflare KV からデータを取得する
        const cloudResult = await fetchFromCloud(randomId, pinCodeStr);
        const bookmarksData = cloudResult.bookmarks;
        const customColorsData = cloudResult.customColors || [];
        
        if (bookmarksData.length <= this.tileCount) {
          // タイル数が不足している場合は空タイルで補完
          while (bookmarksData.length < this.tileCount) {
            bookmarksData.push({
              name: '', url: '', iconUrl: '', color: '', customIcon: '',
              isEmpty: true, id: `tile-${bookmarksData.length + 1}`
            });
          }
          // 【型安全性強化】すべてのidを文字列型に変換し、IDがない場合はインデックスベースで生成
          const safeData = normalizeBookmarks(bookmarksData);
          
          saveBookmarksToStorage(safeData, randomId);
          this.bookmarks = safeData;
          
          // カスタムカラーを復元（空の場合はデフォルト値を使用）
          const restoredColors = customColorsData.length > 0 ? customColorsData : loadCustomColorsFromStorage();
          this.customColors = restoredColors;
          saveCustomColorsToStorage(restoredColors);
          
          // PINコードが正しいことが確認できたので保存しておく
          setCookie('bkms_cloud_pin', pinCodeStr, 30);
          
          return bookmarksData;
        } else {
          console.warn('取得したデータの形式が予期しないものです:', bookmarksData);
          throw new Error('取得したデータの形式が不正です');
        }
      } catch (error) {
        console.error('クラウドAPIからのデータ取得エラー:', error);
        throw error; // 上位の呼び出し元にエラーを伝播
      }
    },

    // === ブックマーク公開機能 (F-021) ===

    // 公開ブクマすURLの検出
    checkPublicViewRedirect() {
      const path = window.location.pathname;
      if (path.startsWith('/pub/')) {
        const pubId = path.substring(5); // '/pub/' を除去
        if (pubId) {
          this.loadPublicBookmarks(pubId);
          return true;
        }
      }
      return false;
    },

    // 公開ブックマークの読み込み
    async loadPublicBookmarks(publishId) {
      this.cloudSaveLoading = true;
      try {
        const data = await fetchPublishedData(publishId);
        // bookmarksがネストされたオブジェクト形式の場合に対応
        let pubBookmarks = data.bookmarks || [];
        if (pubBookmarks && !Array.isArray(pubBookmarks) && Array.isArray(pubBookmarks.bookmarks)) {
          pubBookmarks = pubBookmarks.bookmarks;
        }
        this.bookmarks = Array.isArray(pubBookmarks) ? pubBookmarks : [];
        this.publishSnsUrl = data.snsUrl || '';
        this.publishSnsPlatform = data.snsPlatform || '';
        this.publishSnsUsername = data.snsUsername || '';
        this.isPublicViewMode = true;

        // 利用規約の同意確認
        const hasAgreed = getCookie('bkms_pub_terms_agreed') === 'true';
        if (!hasAgreed) {
          this.isPublicTermsModalOpen = true;
        }
      } catch (error) {
        console.error('[App.vue] 公開データの読み込みに失敗:', error);
        this.showToast('このブクマすは公開されていないか、公開が終了しました', 'error', 5000);
        // トップページにリダイレクト
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 3000);
      } finally {
        this.cloudSaveLoading = false;
      }
    },

    // 公開設定モーダルを表示
    showPublishSettings() {
      if (!this.isCloudSyncActive) return;
      this.isPublishSettingsOpen = true;
    },

    // 現在のクラウドIDを取得するヘルパー
    getCurrentCloudId() {
      const cloudUrl = getCookie('bkms_cloud_url');
      if (!cloudUrl) return '';
      try {
        const urlPath = new URL(cloudUrl).pathname;
        return urlPath.length > 1 ? urlPath.substring(1) : '';
      } catch {
        return '';
      }
    },

    // ブックマーク公開処理
    async handlePublishBookmarks(settings) {
      try {
        // クラウドIDとPINを取得
        const cloudId = this.getCurrentCloudId();
        const pin = getCookie('bkms_cloud_pin');
        if (!cloudId || !pin) {
          this.showToast('クラウド情報が取得できません', 'error');
          this.isPublishSettingsOpen = false;
          return;
        }

        // OGP画像はモーダルのプレビューで生成済み
        const ogpImage = settings.ogpImage || '';

        const result = await publishBookmarks(cloudId, pin, settings.snsUrl, settings.comment, ogpImage);
        
        // 公開URLを構築
        const publishUrl = `${window.location.origin}/pub/${result.publishId}`;
        
        // 公開ページマップに追加
        this.publishedPages = {
          ...this.publishedPages,
          [cloudId]: {
            publishId: result.publishId,
            publishUrl: publishUrl,
            snsUrl: settings.snsUrl || ''
          }
        };
        this.savePublishedPages();

        // 現在のページの公開状態を更新
        this.isPublished = true;
        this.publishId = result.publishId;
        this.publishUrl = publishUrl;
        this.publishSnsUrl = settings.snsUrl || '';

        // 後方互換: Cookie保存
        setCookie('bkms_publish_id', result.publishId, 365);

        // モーダルを切り替え
        this.isPublishSettingsOpen = false;
        this.isPublishUrlModalOpen = true;

        this.showToast('ブクマすを公開しました', 'success');
      } catch (error) {
        console.error('[App.vue] 公開処理エラー:', error);
        this.showToast('公開に失敗しました。もう一度お試しください', 'error');
        this.isPublishSettingsOpen = false;
      }
    },

    // 公開URL表示モーダルを表示（再表示用）
    showPublishUrlModal() {
      if (this.hasAnyPublishedPages) {
        this.isPublishUrlModalOpen = true;
      }
    },

    // 公開中のブクマす一覧モーダルを表示
    showPublishedPagesList() {
      if (this.hasAnyPublishedPages) {
        this.isPublishedPagesListOpen = true;
      }
    },

    // 一覧モーダルから特定ページを非公開にする
    async handleUnpublishFromList(page) {
      try {
        const pin = getCookie('bkms_cloud_pin');
        if (!pin) {
          this.showToast('クラウド情報が取得できません', 'error');
          return;
        }

        await unpublishBookmarks(page.publishId, page.cloudId, pin);

        // 公開ページマップから削除
        const updatedPages = { ...this.publishedPages };
        delete updatedPages[page.cloudId];
        this.publishedPages = updatedPages;
        this.savePublishedPages();

        // 非公開にしたのが現在表示中のページならステート更新
        const currentCloudId = this.getCurrentCloudId();
        if (page.cloudId === currentCloudId) {
          this.isPublished = false;
          this.publishId = '';
          this.publishUrl = '';
          this.publishSnsUrl = '';
        }

        this.showToast('ブクマすを非公開にしました', 'success');

        // 公開ページがなくなったらモーダルを閉じる
        if (Object.keys(this.publishedPages).length === 0) {
          this.isPublishedPagesListOpen = false;
        }
      } catch (error) {
        console.error('[App.vue] 非公開処理エラー:', error);
        this.showToast('非公開処理に失敗しました', 'error');
      }
    },

    // 非公開処理
    async unpublishBookmarksAction() {
      const confirmed = await this.showConfirm('公開URLは無効になります。よろしいですか？', { title: '非公開の確認', okLabel: '非公開にする' });
      if (!confirmed) return;
      
      try {
        const cloudId = this.getCurrentCloudId();
        const pin = getCookie('bkms_cloud_pin');
        if (!cloudId || !pin) {
          this.showToast('クラウド情報が取得できません', 'error');
          return;
        }

        await unpublishBookmarks(this.publishId, cloudId, pin);

        // 公開ページマップから削除
        const updatedPages = { ...this.publishedPages };
        delete updatedPages[cloudId];
        this.publishedPages = updatedPages;
        this.savePublishedPages();

        // 現在のページの公開状態をクリア
        this.isPublished = false;
        this.publishId = '';
        this.publishUrl = '';
        this.publishSnsUrl = '';

        // 後方互換: 旧形式のデータも削除
        const secFlag = location.protocol === 'https:' ? ';SameSite=Lax;Secure' : ';SameSite=Lax';
        const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/' + secFlag;
        document.cookie = 'bkms_publish_id=;' + expired;
        localStorage.removeItem('bkms_publish_id');
        localStorage.removeItem('bkms_publish_url');
        localStorage.removeItem('bkms_publish_sns_url');

        this.showToast('ブクマすを非公開にしました', 'success');
      } catch (error) {
        console.error('[App.vue] 非公開処理エラー:', error);
        this.showToast('非公開処理に失敗しました', 'error');
      }
    },

    // 公開状態の復元（複数公開URL対応）
    restorePublishState() {
      // 新形式（複数公開対応）の読み込み
      try {
        const stored = localStorage.getItem('bkms_published_pages');
        if (stored) {
          this.publishedPages = JSON.parse(stored);
        }
      } catch (e) {
        console.error('公開ページ情報の読み込みに失敗:', e);
        this.publishedPages = {};
      }

      // 旧形式からのマイグレーション
      if (Object.keys(this.publishedPages).length === 0) {
        const pubId = getCookie('bkms_publish_id') || localStorage.getItem('bkms_publish_id');
        if (pubId) {
          const cloudId = this.getCurrentCloudId();
          const storedUrl = localStorage.getItem('bkms_publish_url');
          const publishUrl = (storedUrl && storedUrl !== 'undefined')
            ? storedUrl
            : `${window.location.origin}/pub/${pubId}`;

          if (cloudId) {
            this.publishedPages[cloudId] = {
              publishId: pubId,
              publishUrl: publishUrl,
              snsUrl: localStorage.getItem('bkms_publish_sns_url') || ''
            };
            this.savePublishedPages();
          }
        }
      }

      // 現在のページの公開状態を設定
      this.updatePublishStateForCurrentPage();
    },

    // 現在のページの公開状態を publishedPages マップから同期
    updatePublishStateForCurrentPage() {
      const cloudId = this.getCurrentCloudId();
      const pageInfo = cloudId ? this.publishedPages[cloudId] : null;

      if (pageInfo) {
        this.isPublished = true;
        this.publishId = pageInfo.publishId;
        this.publishUrl = pageInfo.publishUrl;
        this.publishSnsUrl = pageInfo.snsUrl || '';
      } else {
        this.isPublished = false;
        this.publishId = '';
        this.publishUrl = '';
        this.publishSnsUrl = '';
      }
    },

    // 公開ページマップを保存
    savePublishedPages() {
      try {
        localStorage.setItem('bkms_published_pages', JSON.stringify(this.publishedPages));
      } catch (e) {
        console.error('公開ページ情報の保存に失敗:', e);
      }
    },

    // 公開ブクマす利用規約に同意
    acceptPublicTerms() {
      this.isPublicTermsModalOpen = false;
    },

    // 公開ブクマす利用規約から戻る
    goBackFromPublicTerms() {
      this.isPublicTermsModalOpen = false;
      // トップページにリダイレクト
      window.location.href = window.location.origin;
    },

    // === クラウド保存機能 ===
    
    // クラウドに保存ダイアログを表示
    showCloudSaveDialog() {
      console.log('[App.vue] showCloudSaveDialog called');
      // クラウド同期中は何もしない（UIで既に非アクティブになっているはず）
      if (this.isCloudSyncActive) return;
      
      this.isAuthMode = false; // 新規登録モード
      console.log('[App.vue] isAuthMode set to:', this.isAuthMode);
      this.isPinCodeModalOpen = true;
      console.log('[App.vue] isPinCodeModalOpen set to:', this.isPinCodeModalOpen);
    },
    
    // クラウドデータ管理ダイアログを表示
    showCloudManageDialog() {
      this.isCloudDataModalOpen = true;
    },
    
    // PINコード設定後のクラウド保存処理
    handlePinCodeSave(pinCode) {
      // クラウド認証モードの場合
      if (this.pendingCloudAuthId) {
        console.log('クラウド認証のためのPINコード取得:', {
          pinLength: pinCode.length,
          pendingId: this.pendingCloudAuthId
        });
        
        // PINコードを保存
        this.pendingCloudPin = pinCode;
        
        // PINコードモーダルを閉じる
        this.isPinCodeModalOpen = false;
        
        // クラウドデータ取得を開始
        this.fetchCloudData(this.pendingCloudAuthId);
        
        // 認証状態を保存
        setCookie('bkms_cloud_auth', 'true', 30); // 30日間有効
        
        return;
      }
      
      // 通常のクラウド保存モードの場合（既存の処理）
      this.isPinCodeModalOpen = false;
      this.cloudSaveLoading = true;
      
      // 実際のAPIを呼び出し、Cloudflare KVにデータを保存する
      setTimeout(() => {
        try {
          // ランダムIDの生成（暗号学的に安全な乱数を使用）
          const randomBytes = new Uint8Array(7);
          crypto.getRandomValues(randomBytes);
          const randomId = Array.from(randomBytes, (b) => b.toString(36).padStart(2, '0')).join('').substring(0, 9);
          
          // ユニークURLの生成 (Pagesのドメインを使用)
          this.uniqueUrl = window.location.origin + '/' + randomId;
          
          // cloudService を使って Cloudflare KV にデータを保存する
          saveToCloud(randomId, this.bookmarks, pinCode)
          .then(data => {
            console.log('クラウド保存成功:', data);
            
            // URLとPINコードをcookieに保存
            setCookie('bkms_cloud_url', this.uniqueUrl, 365); // 1年間有効
            setCookie('bkms_cloud_auth', 'true', 30); // 30日間有効（認証状態）
            setCookie('bkms_cloud_pin', pinCode, 30); // PINコードも保存
            
            // ブックマークページとして登録（複数ページ対応）
            this.registerCurrentPage(randomId, this.uniqueUrl);
            
            // クラウド保存日時をローカルストレージに記録
            localStorage.setItem('bkms_cloud_saved_at', new Date().toISOString());
            localStorage.setItem('bkms_cloud_id', randomId); // IDも保存
            
            // ローカルストレージにも保存（ページ別キー）
            saveBookmarksToStorage(this.bookmarks, randomId);
            
            // クラウド同期状態を更新
            this.isCloudSyncActive = true;
            
            this.cloudSaveLoading = false;
            
            // ユニークURLモーダルを表示
            this.isUniqueUrlModalOpen = true;
          })
          .catch(error => {
            console.error('クラウド保存API呼び出しエラー:', error);
            this.showToast(`クラウドへの保存に失敗しました: ${error.message} 再試行してください。`, 'error');
            this.cloudSaveLoading = false;
          });
        } catch (error) {
          console.error('クラウド保存エラー:', error);
          this.showToast('クラウドへの保存に失敗しました。再試行してください。', 'error');
          this.cloudSaveLoading = false;
        }
      }, 500); // 処理時間を短縮
    },
    
    // クラウドへデータを保存する処理
    scheduleCloudSync() {
      // クラウド同期を呼び出す
      console.log('クラウドにデータを保存しています...');
      
      // クラウド同期状態をチェック
      this.checkCloudSyncState();
      
      // クラウド同期が有効な場合のみ実行
      if (this.isCloudSyncActive) {
        // syncToCloudメソッドを呼び出す
        this.syncToCloud();
        
        // クラウド保存日時をローカルに記録
        localStorage.setItem('bkms_cloud_saved_at', new Date().toISOString());
      } else {
        console.warn('クラウド同期が無効です。保存をスキップします。');
      }
    },

    // PINコードモーダルをキャンセル
    closePinCodeModal() {
      // クラウド認証中にキャンセルされた場合の復帰処理
      const wasCloudAuth = !!this.pendingCloudAuthId;

      this.isPinCodeModalOpen = false;
      this.isAuthMode = false; // モードをリセット
      // 認証待ちの状態もクリア
      this.pendingCloudAuthId = '';

      if (wasCloudAuth) {
        // クラウド同期状態をリセット
        this.isCloudSyncActive = false;
        this.isCloudOfflineMode = false;
        this.cloudFetchFailed = false;
        this.cloudSaveLoading = false;
        // クラウドURL cookieをクリアしてリダイレクトループを防止
        clearCloudCookies();
        localStorage.removeItem('bkms_pending_cloud_id');
        localStorage.removeItem('bkms_cloud_id');
        localStorage.removeItem('bkms_offline_since');
        localStorage.removeItem('bkms_offline_modified');

        // URLをルートに書き換え（リロードなし）
        window.history.replaceState(null, '', '/');

        // ローカルデータで初期化（クラウドIDなしの汎用ローカルデータ）
        this.bookmarks = initializeBookmarks(this.tileCount, null);

        this.showToast('ローカルモードで起動します。クラウドデータへは再度URLにアクセスすることで接続できます。', 'info');
      }
    },
    
    // ユニークURLモーダルを閉じる
    closeUniqueUrlModal() {
      this.isUniqueUrlModalOpen = false;
      // 閉じるボタンを押したときにリダイレクト
      window.location.href = this.uniqueUrl;
    },
    
    // クラウドデータ管理モーダルを閉じる
    closeCloudDataModal() {
      this.isCloudDataModalOpen = false;
    },
    
    // クラウドデータの削除
    async deleteCloudData() {
      // PINコード認証のためのプロンプト表示
      const cloudId = localStorage.getItem('bkms_cloud_id');
      if (!cloudId) {
        this.showToast('クラウド同期情報が見つかりません。', 'error');
        this.isCloudDataModalOpen = false;
        return;
      }
      
      const savedPin = getCookie('bkms_cloud_pin');
      const pinCode = savedPin;
      
      if (!pinCode) {
        this.showToast('PINコードが見つかりません。再度クラウド接続してください。', 'error');
        this.isCloudDataModalOpen = false;
        return;
      }
      
      const confirmed = await this.showConfirm('クラウドデータを削除すると、他のデバイスからこのブックマークセットにアクセスできなくなります。\n\n本当に削除しますか？', { title: 'クラウドデータ削除の確認', okLabel: '削除', dangerous: true });
      
      if (!confirmed) {
        this.isCloudDataModalOpen = false;
        return;
      }
      
      this.cloudSaveLoading = true;
      
      // cloudService を使って Cloudflare KV のデータを削除する
      deleteFromCloud(cloudId, pinCode)
      .then(data => {
        console.log('クラウドデータ削除成功:', data);
        
        // クラウド同期状態をリセット
        this.isCloudSyncActive = false;
        
        // cookieからユニークURLと認証情報を削除
        clearCloudCookies();
        
        // ローカルストレージからクラウド関連のデータを削除
        localStorage.removeItem('bkms_cloud_saved_at');
        localStorage.removeItem('bkms_cloud_id');
        localStorage.removeItem('bkms_last_cloud_access');
        
        // ブックマークページ一覧から該当ページを削除
        this.bookmarkPages = this.bookmarkPages.filter(page => page.id !== cloudId);
        this.saveBookmarkPages();
        this.currentPageId = '';
        
        console.log('クラウドデータとの同期を解除しました');
        
        this.showToast('クラウドデータを削除しました', 'success');
        this.isCloudDataModalOpen = false;
        this.cloudSaveLoading = false;
        
        // クラウド同期状態を更新
        this.checkCloudSyncState();
        
        // トップページにリダイレクト
        window.location.href = window.location.origin;
      })
      .catch(error => {
        console.error('クラウドデータ削除エラー:', error);
        this.showToast(`データの削除に失敗しました: ${error.message}`, 'error');
        this.isCloudDataModalOpen = false;
        this.cloudSaveLoading = false;
      });
    },
    
    // クラウド同期状態を確認するメソッド
    checkCloudSyncState() {
      // クラウドURLとPINが存在するか確認
      const cloudUrl = getCookie('bkms_cloud_url');
      const isAuthenticated = getCookie('bkms_cloud_auth') === 'true';
      
      // クラウドIDが保存されているか確認
      const cloudId = localStorage.getItem('bkms_cloud_id');
      
      if (cloudUrl && isAuthenticated && cloudId) {
        // クラウド同期が有効な状態とする
        this.isCloudSyncActive = true;
        console.log('クラウド同期が有効になっています:', {
          url: cloudUrl,
          authenticated: isAuthenticated,
          id: cloudId
        });
      } else {
        this.isCloudSyncActive = false;
        console.log('クラウド同期が無効です');
      }
    },
    // プライバシーポリシーを表示
    showPrivacyPolicy() {
      this.isPrivacyPolicyOpen = true;
    },
    // お問い合わせページを開く
    openContactPage() {
      window.open('https://txt365.org/contact?service=bkms', '_blank', 'noopener,noreferrer');
    },
    
    // プライバシーポリシーを閉じる
    closePrivacyPolicy() {
      this.isPrivacyPolicyOpen = false;
    },
    // BKMSについてモーダルを閉じる
    closeAboutPage() {
      this.isAboutPageOpen = false;
    },
    // 利用規約への同意確認を行うメソッド
    checkTermsAgreement() {
      // cookieから同意状態を確認
      const hasAgreed = getCookie('bkms_terms_agreed') === 'true';
      
      // ローカルストレージからも確認（バックアップとして）
      const localStorageAgreement = localStorage.getItem('bkms_terms_agreed') === 'true';
      
      // ブックマークJSONが存在するか確認
      const currentCloudId = this.getCurrentCloudId();
      const pageKey = getBookmarkStorageKey(currentCloudId);
      const hasSavedBookmarks = localStorage.getItem(pageKey) !== null || localStorage.getItem('bookmarks') !== null;
      
      // どれかの方法で同意済みか、すでにJSONデータが存在する場合は利用可能に
      if (hasAgreed || localStorageAgreement || hasSavedBookmarks) {
        this.isTermsModalOpen = false;
      } else {
        // 未同意でデータもない場合は利用規約ポップアップを表示
        this.isTermsModalOpen = true;
      }
    },
    // 利用規約同意時の処理
    acceptTerms() {
      console.log('[App.vue] acceptTerms called');
      // 規約に同意したことを記録
      setCookie('bkms_terms_agreed', 'true', 365); // 1年間有効
      
      // ローカルストレージにも保存（バックアップとして）
      localStorage.setItem('bkms_terms_agreed', 'true');
      
      // モーダルを閉じる
      this.isTermsModalOpen = false;
      
      console.log('利用規約に同意しました');
      
      // クラウドリダイレクトが保留中の場合は認証処理に進む
      const pendingCloudId = localStorage.getItem('bkms_pending_cloud_id');
      console.log('[App.vue] acceptTerms - pendingCloudId:', pendingCloudId);
      if (pendingCloudId) {
        console.log('[App.vue] acceptTerms - proceedToCloudAuthenticationを呼び出します');
        this.proceedToCloudAuthentication(pendingCloudId);
        localStorage.removeItem('bkms_pending_cloud_id'); // 保留IDをクリア
        console.log('[App.vue] acceptTerms - pendingCloudIdをクリアしました');
      } else {
        console.log('[App.vue] acceptTerms - pendingCloudIdが見つかりません');
      }
    },
    // ブックマークページ一覧を読み込み
    loadBookmarkPages() {
      try {
        const stored = localStorage.getItem('bkms_bookmark_pages');
        if (stored) {
          this.bookmarkPages = JSON.parse(stored);
        }
      } catch (e) {
        console.error('ブックマークページ一覧の読み込みに失敗:', e);
        this.bookmarkPages = [];
      }
      
      // 現在のページIDを設定
      const path = window.location.pathname;
      const currentId = path.length > 1 ? path.substring(1) : '';
      if (currentId) {
        this.currentPageId = currentId;
        // 現在のページのlastAccessedを更新
        this.updatePageLastAccessed(currentId);
      }
    },
    
    // ブックマークページ一覧を保存
    saveBookmarkPages() {
      try {
        localStorage.setItem('bkms_bookmark_pages', JSON.stringify(this.bookmarkPages));
      } catch (e) {
        console.error('ブックマークページ一覧の保存に失敗:', e);
      }
    },
    
    // 現在のクラウドURLをブックマークページとして登録
    registerCurrentPage(id, url) {
      const existingIndex = this.bookmarkPages.findIndex(page => page.id === id);
      const now = new Date().toISOString();
      
      if (existingIndex !== -1) {
        // 既存ページの更新
        this.bookmarkPages[existingIndex].url = url;
        this.bookmarkPages[existingIndex].lastAccessed = now;
      } else {
        // 新しいページを追加
        this.bookmarkPages.push({
          id: id,
          name: '',
          url: url,
          lastAccessed: now
        });
      }
      
      this.currentPageId = id;
      this.saveBookmarkPages();
    },
    
    // ページの最終アクセス日時を更新
    updatePageLastAccessed(id) {
      const targetPage = this.bookmarkPages.find(page => page.id === id);
      if (targetPage) {
        targetPage.lastAccessed = new Date().toISOString();
        this.saveBookmarkPages();
      }
    },
    
    // 最後にアクセスしたページを取得
    getLastAccessedPage() {
      if (this.bookmarkPages.length === 0) return null;
      return this.bookmarkPages.reduce((latest, page) => {
        if (!latest) return page;
        return new Date(page.lastAccessed || 0) > new Date(latest.lastAccessed || 0) ? page : latest;
      }, null);
    },
    
    // 新しいブックマークページを作成
    createNewBookmarkPage() {
      this.isBookmarkPagesModalOpen = false;
      
      // ペンディング中のクラウド同期をキャンセル
      if (this.cloudSyncTimer) {
        clearTimeout(this.cloudSyncTimer);
        this.cloudSyncTimer = null;
      }
      
      // 現在のページをページ一覧に登録（未登録の場合）
      const currentCloudUrl = getCookie('bkms_cloud_url');
      if (currentCloudUrl) {
        const path = new URL(currentCloudUrl).pathname;
        const currentId = path.length > 1 ? path.substring(1) : '';
        if (currentId) {
          // 現在のデータをページ別キーに確実に保存
          saveBookmarksToStorage(this.bookmarks, currentId);
          this.registerCurrentPage(currentId, currentCloudUrl);
        }
      }
      
      // 現在のクラウド同期を解除してルートに移動
      // cookieからクラウドURL情報をクリア（ページ一覧は保持）
      clearCloudCookies();
      
      // クラウド同期状態をリセット
      this.isCloudSyncActive = false;
      
      // ローカルストレージのクラウド関連データをクリア
      localStorage.removeItem('bkms_cloud_id');
      localStorage.removeItem('bkms_cloud_saved_at');
      localStorage.removeItem('bkms_last_cloud_access');
      
      // ローカルのブックマークデータをクリア（新しいページ用）
      // 注意: ページ別キーは保持し、旧形式の共通キーのみクリアする
      localStorage.removeItem('bookmarks');
      
      // 新しいページ作成フラグを設定（リダイレクト防止）
      sessionStorage.setItem('bkms_creating_new_page', 'true');
      
      // ルートページにリダイレクト
      window.location.href = window.location.origin;
    },
    
    // ブックマークページ管理モーダルを表示
    showBookmarkPagesModal() {
      this.loadBookmarkPages(); // 最新データを読み込み
      this.isBookmarkPagesModalOpen = true;
    },
    
    // ブックマークページを切り替え
    switchBookmarkPage(page) {
      this.isBookmarkPagesModalOpen = false;
      
      // ペンディング中のクラウド同期をキャンセル（旧ページのデータが誤送信されるのを防止）
      if (this.cloudSyncTimer) {
        clearTimeout(this.cloudSyncTimer);
        this.cloudSyncTimer = null;
      }
      
      // 現在のページのデータをページ別キーに確実に保存してから切り替え
      const currentCloudId = this.getCurrentCloudId();
      if (currentCloudId) {
        saveBookmarksToStorage(this.bookmarks, currentCloudId);
      }
      
      // 切り替え先ページのURLにリダイレクト
      if (page.url) {
        // cookie を切り替え先のURLに更新
        setCookie('bkms_cloud_url', page.url, 365);
        
        // lastAccessedを更新
        this.updatePageLastAccessed(page.id);
        
        // リダイレクトループ検出用フラグを設定
        sessionStorage.setItem('bkms_redirect_target_url', page.url);
        
        window.location.href = page.url;
      }
    },
    
    // ブックマークページ名を変更
    renameBookmarkPage({ id, name }) {
      const targetPage = this.bookmarkPages.find(page => page.id === id);
      if (targetPage) {
        targetPage.name = name.substring(0, 20); // 最大20文字
        this.saveBookmarkPages();
      }
    },
    
    // ログレベル設定
    setLogLevel(level) {
      logger.setLogLevel(level);
    },
    
    // 現在のログレベルを取得
    getLogLevel() {
      return localStorage.getItem('bkms_log_level') || 'DEBUG';
    },
  }
}
</script>

<style>
/* Vuetifyスタイル以外の追加カスタムスタイル */
.v-application {
  font-family: Helvetica Neue, Arial, sans-serif;
  line-height: 1.6;
}

.custom-search-container {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 50px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  margin-top: 32px;
  height: 48px;
  width: 100%;
}

/* 検索コンテナのモバイル表示調整 */
.search-col {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/* v-mainのパディングを調整 */
.v-main {
  padding-top: 64px !important;
  /* スマホスクロール最適化 */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  will-change: scroll-position;
}

/* モバイル用のメインコンテンツ調整 */
@media (max-width: 768px) {
  .v-main {
    padding-top: 64px !important;
    margin-top: 0 !important;
    position: relative !important;
    z-index: 1 !important;
  }
}

/* コンテナのスクロール最適化 */
.v-container {
  /* スマホでのスクロール性能向上 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.engine-select {
  position: relative;
  width: 120px;
  padding: 0 12px 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  cursor: pointer;
  background-color: white;
  user-select: none;
}

.dropdown-arrow {
  font-size: 12px;
  color: #777;
  margin-left: 4px;
}

.hidden-select {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.divider {
  width: 1px;
  height: 28px;
  background-color: #ddd;
  margin: 0 4px;
}

.search-input-area {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
}

.custom-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 16px;
  font-size: 16px;
  color: #333;
}

.custom-input::placeholder {
  color: #999;
}

/* タイル一覧 */
.tiles-container-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 24px auto 0;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.tiles-container {
  display: grid;
  padding: 15px;
  width: 100%;
  box-sizing: border-box;
  justify-items: center;  /* タイルアイテム自体を中央に */
  align-items: center;    /* 垂直方向も中央に */
  grid-template-columns: repeat(auto-fill, 100px);  /* 固定幅のカラム */
  gap: 20px;             /* タイル間の間隔を統一 */
  justify-content: center; /* グリッド全体を中央に */
}

/* モバイル用非ドラッガブル版のレイアウト */
.mobile-tiles {
  display: grid;
  width: 100%;
  box-sizing: border-box;
  justify-items: center;
  align-items: center;
  grid-template-columns: repeat(auto-fill, 90px);
  gap: 16px;
  padding: 15px;
  justify-content: center;
  touch-action: pan-x pan-y !important; /* スクロールのみ許可 */
}

/* モバイル用タイルの親要素にポインターイベント制御を追加 */
.mobile-tiles > div {
  margin: 0;
  padding: 0;
  /* スクロール改善：通常時はスクロールを優先 */
  touch-action: manipulation !important;
  -webkit-touch-action: manipulation !important;
  pointer-events: all;
  position: relative;
}

/* 並び替えモード時のみtouch-actionを制限 */
.sort-mode .mobile-tiles > div {
  touch-action: none !important;
  -webkit-touch-action: none !important;
}

/* タイルがドラッグされているときのスタイル */
.ghost-tile {
  opacity: 0.5;
  background: #c8ebfb;
  border: 1px dashed #e9d0ca;
  box-shadow: none;
  border-radius: 8px;
}

/* スマートフォン操作性改善：完了ボタン */
.complete-button {
  position: fixed !important;
  bottom: 32px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  z-index: 1000;
  min-width: 280px !important;
  width: 75vw !important;
  max-width: 420px !important;
  height: 50px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.15),
    0 3px 10px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
  border: none !important;
  border-radius: 25px !important;
  letter-spacing: 0.5px !important;
  backdrop-filter: blur(10px);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
  position: relative;
}

.complete-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
  transition: left 0.4s ease-out;
}

.complete-button:hover::before {
  left: 100%;
}

.complete-button .v-btn__content {
  padding: 0 24px !important;
  gap: 8px !important;
  z-index: 1;
  position: relative;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.complete-button__text {
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
}

.complete-button .v-icon {
  font-size: 18px !important;
  margin-left: 4px !important;
}

.complete-button:hover {
  transform: translateX(-50%) translateY(-2px) scale(1.01) !important;
  box-shadow: 
    0 12px 35px rgba(0, 0, 0, 0.18),
    0 5px 15px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.complete-button:active {
  transform: translateX(-50%) translateY(0px) scale(0.98) !important;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
  transition: all 0.1s ease-out !important;
}

/* 並び替えモード用スタイル */
.complete-button--sort {
  background: linear-gradient(135deg, #FF7043 0%, #FF9800 50%, #FFC107 100%) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

.complete-button--sort:hover {
  background: linear-gradient(135deg, #FF8A65 0%, #FFB74D 50%, #FFD54F 100%) !important;
}

/* リンク編集モード用スタイル */
.complete-button--edit {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 50%, #81C784 100%) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

.complete-button--edit:hover {
  background: linear-gradient(135deg, #66BB6A 0%, #81C784 50%, #A5D6A7 100%) !important;
}

/* ダークモード対応 */
@media (prefers-color-scheme: dark) {
  .complete-button {
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.4),
      0 3px 10px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
  }
  
  .complete-button:hover {
    box-shadow: 
      0 12px 35px rgba(0, 0, 0, 0.5),
      0 5px 15px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
  }
  
  .complete-button::before {
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  }
}

/* モバイル対応 */
@media (max-width: 480px) {
  .complete-button {
    min-width: 260px !important;
    width: 80vw !important;
    height: 48px !important;
    bottom: 24px !important;
    border-radius: 24px !important;
  }
  
  .complete-button__text {
    font-size: 15px !important;
    letter-spacing: 0.3px !important;
  }
  
  .complete-button .v-icon {
    font-size: 16px !important;
  }
  
  .complete-button:hover {
    transform: translateX(-50%) translateY(-1px) scale(1.005) !important;
  }
}

/* 小型デバイス対応 */
@media (max-width: 360px) {
  .complete-button {
    min-width: 220px !important;
    width: 85vw !important;
    height: 46px !important;
    left: 7.5vw !important;
    right: 7.5vw !important;
    transform: none !important;
    border-radius: 23px !important;
  }
  
  .complete-button__text {
    font-size: 14px !important;
    letter-spacing: 0.2px !important;
  }
  
  .complete-button .v-icon {
    font-size: 15px !important;
  }
  
  .complete-button:hover {
    transform: translateY(-1px) scale(1.005) !important;
  }
  
  .complete-button:active {
    transform: translateY(0px) scale(0.99) !important;
  }
}

/* 古いスタイルを削除 */
.complete-fab {
  display: none;
}

.complete-text {
  display: none;
}

/* ヘッダーのスタイル調整 */
.header-container {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
  width: 100% !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.header-content-container {
  max-width: 1200px !important;
  margin: 0 auto !important;
}

.header-inner-flex {
  display: flex;
  align-items: center;
  width: 100%;
  height: 64px;
  position: relative;
}

/* モバイル用ヘッダー固定の強化 */
@media (max-width: 768px) {
  .header-container {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 1001 !important;
    width: 100% !important;
    -webkit-transform: translateZ(0) !important;
    transform: translateZ(0) !important;
    -webkit-backface-visibility: hidden !important;
    backface-visibility: hidden !important;
  }
}

/* モードバッジを画面中央に絶対配置 */
.mode-badge-absolute {
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.mode-badge {
  height: 24px !important;
  font-size: 12px !important;
  font-weight: bold !important;
}

/* ロゴリンク */
.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
}

/* 公開バッジ（2行表示） */
.public-badge-container {
  display: flex;
  align-items: center;
}

.public-badge-chip {
  height: auto !important;
  padding: 4px 12px !important;
}

.public-badge-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.3;
}

.public-badge-title {
  font-size: 12px;
  font-weight: bold;
}

.public-badge-creator {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  opacity: 0.9;
}

.public-badge-sns-icon {
  margin-right: 1px;
}

.public-badge-creator-link {
  color: white !important;
  text-decoration: none;
}

.public-badge-creator-link:hover {
  text-decoration: underline;
}

.public-badge-creator-unknown {
  color: rgba(255, 255, 255, 0.85);
}

/* 編集モード・並び替えモード時のタイル視覚効果 */
.tile.edit-mode-active {
  border: 2px solid #4CAF50 !important;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.3) !important;
}

.tile.sort-mode-active {
  border: 2px solid #FF9800 !important;
  box-shadow: 0 0 8px rgba(255, 152, 0, 0.3) !important;
}

/* モバイル対応 */
@media (max-width: 767px) {
  .engine-select {
    width: 100px;
    padding-left: 12px;
  }
  
  .custom-input {
    font-size: 14px;
  }
  
  .custom-search-container {
    margin-left: auto;
    margin-right: auto;
    max-width: calc(100% - 32px);
  }
  
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 16px;
    padding: 10px;
  }
}

@media (min-width: 768px) {
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(100px, 100px));
    gap: 30px;
  }
}

@media (min-width: 900px) {
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    max-width: 1200px;
  }
}

/* クラウドデータ読み込み中のローディングオーバーレイ */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  color: #333;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ファビコンはグレー化禁止 */
link[rel="icon"],
link[rel="shortcut icon"],
link[rel="apple-touch-icon"] {
  filter: none !important;
  -webkit-filter: none !important;
}

head link[rel*="icon"] {
  filter: none !important;
  -webkit-filter: none !important;
}

/* ファビコン画像の強制カラー保持 */
img[src*="favicon"],
img[src*="icon"] {
  filter: none !important;
  -webkit-filter: none !important;
}

/* バーガーメニューのTabler Icons */
.menu-icon {
  color: #555;
  flex-shrink: 0;
  margin-right: 6px;
}

/* Vuetify v-iconをTabler Iconsと同じ位置に揃える */
.custom-menu-list .v-list-item .v-icon.menu-icon {
  margin-inline-end: 6px;
  margin-inline-start: 0;
  padding: 0;
  width: 20px;
  height: 20px;
}

.custom-menu-list .v-list-item--prepend .v-list-item__prepend {
  margin-inline-end: 6px;
  --v-list-item-prepend-width: 0px;
}
</style>
